import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { ContentMasterMerkmalMultiEditor } from '../components/content-master/ContentMasterMerkmalMultiEditor';
import ContentMasterProBadge from '../components/content-master/ContentMasterProBadge';
import ContentMasterApplyWarningModal, {
  isApplyWarningDismissed,
} from '../components/content-master/ContentMasterApplyWarningModal';
import { ContentMasterProductThumb } from '../components/content-master/ContentMasterProductThumb';
import ContentMasterOtherSourcesBadge from '../components/content-master/ContentMasterOtherSourcesBadge';
import MarketplaceFieldsSection, {
  ACTION_BAR_ERRORS_SLOT_ID,
  type MarketplaceEditorState,
  type MarketplaceFieldsSectionHandle,
} from '../components/content-master/MarketplaceFieldsSection';
import AdditionalContextInput, {
  type AdditionalContextState,
  buildAdditionalContext,
  emptyAdditionalContextState,
  hasParsingAdditionalContextFiles,
  ADDITIONAL_CONTEXT_MAX_LENGTH,
} from '../components/content-master/AdditionalContextInput';
import BulkGenerateConfirmModal, {
  type BulkGenerateConfirmParams,
} from '../components/content-master/BulkGenerateConfirmModal';
import FieldLengthCounter from '../components/content-master/FieldLengthCounter';
import {
  ContentMasterEyeIcon,
  HTML_PREVIEW_CORE_FIELDS,
  useHtmlPreview,
} from '../components/content-master/HtmlPreview';
import ArrowLeftIcon from '../components/Icons/ArrowLeftIcon';
import Modal from '../components/ui/Modal';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { usePermissions } from '../hooks/usePermissions';
import { useContentMasterEntitlements } from '../hooks/useContentMasterEntitlements';
import type { ContentMasterFieldVersionView } from '../api/generated/models/ContentMasterFieldVersionView';
import type { ContentMasterLocalProductItemView } from '../api/generated/models/ContentMasterLocalProductItemView';
import { ContentMasterProductView } from '../api/generated/models/ContentMasterProductView';
import { ContentMasterWorkspacePlatformView } from '../api/generated/models/ContentMasterWorkspacePlatformView';
import { MarketplaceProductFieldsView } from '../api/generated/models/MarketplaceProductFieldsView';
import { ContentMasterService } from '../api/generated/services/ContentMasterService';
import { ContentMasterSaveResultView } from '../api/generated/models/ContentMasterSaveResultView';
import type { ContentMasterGenerateAllowedOptionView } from '../api/generated/models/ContentMasterGenerateAllowedOptionView';
import type { ContentMasterWawiExtendedFieldView } from '../api/generated/models/ContentMasterWawiExtendedFieldView';
import {
  CONTENT_MASTER_CORE_FIELD_KEYS,
  type ContentMasterCoreFieldKey,
  ContentMasterDraftFieldsPayload,
  type ContentMasterGenerateAllowedValues,
  ContentMasterPostLanguage,
  type ContentMasterSource,
  applyContentMasterDraft,
  applyContentMasterListItemStatus,
  applyContentMasterListItemTitle,
  clearContentMasterDetail,
  editableFields,
  fetchContentMasterProductDetail,
  fetchContentMasterWorkspaceConfig,
  generateAllContentMasterFields,
  generateContentMasterField,
  saveContentMasterDraft,
  setContentMasterQuotaModalVisible,
  setContentMasterSubscriptionInsufficientModalVisible,
  setPendingContentMasterHighlight,
  setSkipNextContentMasterListFetch,
  translateContentMasterFields,
} from '../store/slices/contentMasterSlice';
import { addNotification, clearAllNotifications } from '../store/slices/notificationSlice';
import { submitMarketplaceListingGenerateSync } from '../store/slices/marketplaceListingSlice';
import { store } from '../store/store';
import {
  isWawiLanguageCodeAllowed,
  pickDefaultWawiLanguageCode,
  wawiLanguageLabel,
  wawiLanguageToApiValue,
} from '../utils/contentMasterWawiLanguage';
import {
  getContentMasterLanguageState,
  pickContentMasterDefaultLanguage,
  type ContentMasterLanguageState,
} from '../utils/contentMasterLanguageState';
import { type ContentMasterListLocationState } from '../utils/contentMasterListUrl';
import {
  contentMasterStatusLabelKey,
  getContentMasterStatusClassName,
} from '../utils/contentMasterProcessingStatus';
import {
  buildContentMasterChannelGroups,
  buildContentMasterChannelOptions,
  formatContentMasterChannelMeta,
  isContentMasterSourceLiteral,
  pickDefaultContentMasterChannelId,
  readLastSelectedChannelId,
  resolveContentMasterSourceLabel,
  writeLastSelectedChannelId,
} from '../utils/contentMasterWorkspace';
import {
  attributeDisplayValue,
  attributeLikeLabel,
  canAiGenerateAttributeLike,
  canAiGenerateMerkmal,
  collectExtendedFieldKeys,
  formatMerkmalLabels,
  getMerkmalCurrentIds,
  isAttributeLikeField,
  isMerkmalField,
  joinMerkmalCsvIds,
  merkmalLabel,
  parseMerkmalCsvIds,
  selectArticleAttributes,
  selectEigeneFelder,
  selectMerkmale,
} from '../utils/contentMasterWawiExtendedFields';
import {
  estimateAdditionalContextTokens,
  estimateContentMasterAiTokens,
} from '../utils/contentMasterTokenEstimate';
import { filterGenerateFieldsForEntitlements } from '../utils/contentMasterEntitlements';

/** GET /content-master/products/local - API max page size (500). */
const IN_PROGRESS_LOCAL_QUEUE_LIMIT = 500;
const IN_PROGRESS_QUEUE_URL_PARAM = 'inProgressPos';

type FieldValues = Record<ContentMasterCoreFieldKey, string>;
type SelectedVersionMap = Partial<Record<string, string>>;
type VersionSelectionMap = Partial<Record<string, boolean>>;
type DirtyFieldsMap = Partial<Record<string, boolean>>;

/**
 * The right-column editor operates in one of two mutually-exclusive modes,
 * toggled by the `Generate Content` / `Translate` segmented buttons in the
 * right column header:
 *
 *   - `'generate'` - existing AI-generation flow. Header CTA is `Generate All
 *     Fields`, per-field link is `Generate field`. Save / Apply send the
 *     `language` query as the page language (i.e. what the left column shows).
 *   - `'translate'` - translation flow. Header CTA is `Translate All Fields`,
 *     per-field link is `Translate`. Save / Apply send `language =
 *     targetLanguage`, since the values just placed in the right column are a
 *     translation INTO target.
 */
type ContentMasterEditorMode = 'generate' | 'translate';

/**
 * Sentinel id stored in `selectedVersionIds` to represent the synthetic "Original"
 * pill - the original Wawi value of a field. Real version ids are UUIDs so this
 * cannot collide. `getCoreOriginalValue` / `getExtensionReferenceValue` already fall
 * through to the Wawi value when no real version matches the selected id, so the
 * sentinel needs no special handling there beyond the active-state highlight.
 */
const ORIGINAL_VERSION_ID = '__content_master_original__';

const FIELD_LABELS: Record<ContentMasterCoreFieldKey, string> = {
  title: 'title',
  shortDescription: 'shortDescription',
  description: 'description',
  metaTitleTag: 'titleTag',
  metaKeywords: 'content_master_meta_keywords',
  metaDescription: 'content_master_meta_description',
  urlPath: 'urlPath',
};

const createEmptyFieldValues = (): FieldValues => ({
  title: '',
  shortDescription: '',
  description: '',
  metaTitleTag: '',
  metaKeywords: '',
  metaDescription: '',
  urlPath: '',
});

/**
 * Builds `fieldAllowedValues` for POST /content-master/generate in the
 * `{value, label?}` shape. Driven by the unified `wawiExtendedFields`
 * array - both attribute-like (`attribut` + `eigene`) varchar picklists and
 * Merkmal kMerkmalWert lists are emitted, only for keys present in
 * `fieldKeys`. Items of unsupported types are silently skipped.
 */
const buildFieldAllowedValuesForGenerate = (
  detail: ContentMasterProductView,
  fieldKeys: string[]
): ContentMasterGenerateAllowedValues | undefined => {
  const wanted = new Set(fieldKeys);
  const out: ContentMasterGenerateAllowedValues = {};

  for (const f of detail.wawiExtendedFields ?? []) {
    const name = f.fieldApiName;
    if (!name || !wanted.has(name)) {
      continue;
    }
    if (isAttributeLikeField(f)) {
      const opts = (f.allowedValues ?? [])
        .filter((x): x is string => typeof x === 'string' && x.length > 0)
        .map<ContentMasterGenerateAllowedOptionView>((value) => ({ value }));
      if (opts.length) {
        out[name] = opts;
      }
      continue;
    }
    if (isMerkmalField(f)) {
      const opts = (f.merkmalWertOptions ?? [])
        .map<ContentMasterGenerateAllowedOptionView | null>((o) => {
          if (o.kMerkmalWert == null) {
            return null;
          }
          const value = String(o.kMerkmalWert);
          const label =
            typeof o.label === 'string' && o.label.trim().length > 0
              ? o.label
              : undefined;
          return label ? { value, label } : { value };
        })
        .filter((x): x is ContentMasterGenerateAllowedOptionView => x !== null);
      if (opts.length) {
        out[name] = opts;
      }
    }
  }

  return Object.keys(out).length > 0 ? out : undefined;
};

const CORE_FIELD_KEY_SET = new Set<string>(CONTENT_MASTER_CORE_FIELD_KEYS);

const ContentMasterSparkleIcon: React.FC = () => (
  <svg
    className="content-master-generate-all-btn__icon"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12 2l1.09 3.36L16.5 6.5l-3.41 1.14L12 11l-1.09-3.36L7.5 6.5l3.41-1.14L12 2zM19 15l.66 2.03 2.12.71-2.12.71L19 20.5l-.66-2.03-2.12-.71 2.12-.71L19 15zM5 8l.55 1.69 1.77.59-1.77.59L5 12.5 4.45 10.81 2.68 10.22l1.77-.59L5 8z"
      fill="currentColor"
    />
  </svg>
);

const getVersionTimestamp = (version: ContentMasterFieldVersionView): number => {
  const sourceDate = version.updatedAt || version.createdAt;
  if (!sourceDate) {
    return 0;
  }

  const timestamp = new Date(sourceDate).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

/**
 * Read the version map for the language this product detail represents
 * (`detail.language` is the language echoed back by `GET /content-master/product`,
 * so it always matches one of the keys in `languageVersions`). Used by every
 * caller that previously read the deprecated top-level `detail.versions`
 * shortcut - the new contract is to go through `languageVersions[lang]` so
 * the page is guaranteed to render data for one specific language slice and
 * never accidentally mixes languages.
 */
const getActiveLanguageVersions = (
  product: ContentMasterProductView | null
): Record<string, Array<ContentMasterFieldVersionView>> => {
  const lang = product?.language ?? '';
  if (!lang) {
    return {};
  }
  return product?.languageVersions?.[lang]?.versions ?? {};
};

const getMaxDetailModifiedMs = (product: ContentMasterProductView | null): number => {
  const versionsByField = getActiveLanguageVersions(product);
  let max = 0;
  for (const list of Object.values(versionsByField)) {
    if (!Array.isArray(list)) {
      continue;
    }
    for (const v of list) {
      const ts = getVersionTimestamp(v);
      if (ts > max) {
        max = ts;
      }
    }
  }
  return max;
};

const formatDetailDate = (timestampMs: number): string => {
  if (!timestampMs) {
    return '-';
  }
  const d = new Date(timestampMs);
  if (Number.isNaN(d.getTime())) {
    return '-';
  }
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatVersionLabel = (version: ContentMasterFieldVersionView): string => {
  const sourceDate = version.updatedAt || version.createdAt;
  if (!sourceDate) {
    return '-';
  }

  const parsedDate = new Date(sourceDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

type FieldInitState = {
  selectedId: string;
  useVersion: boolean;
  dirty: boolean;
  seedValue: string;
};

/**
 * "Neutral" right-column seed for a field - the value to drop into the editor
 * when the user is in a clean, non-dirty state (DONE on entry, NEW, click on
 * the current pill, click on Original).
 *
 *   - Merkmale (multi-value): the canonical CSV of currently-assigned
 *     `kMerkmalWert` ids (`draftValue`, falling back to `kMerkmalWerte`). The
 *     multi-select reads this CSV to render which checkboxes start checked;
 *     an empty string correctly represents "no values assigned".
 *   - Attributes with fixed options (also <select>-based): seed with the
 *     attribute's current display value, otherwise the HTML `<select>` would
 *     auto-default to the first option (HTML semantics - `value=""` matches
 *     no real option) and falsely suggest a value the user did not pick.
 *   - Everything else (textarea / text input / number / date inputs): empty
 *     string. Inputs/textareas have no auto-default and the empty value
 *     correctly conveys "no draft change".
 *
 * Save semantics are unaffected - `dirty=false` keeps these neutral values
 * out of the payload entirely, even though the right-column control happens
 * to display a non-empty string for option-based fields.
 */
const computeNeutralSeed = (
  detail: ContentMasterProductView,
  fieldKey: string
): string => {
  const ext = (detail.wawiExtendedFields ?? []).find(
    (f) => f.fieldApiName === fieldKey
  );
  if (!ext) {
    return '';
  }
  if (isMerkmalField(ext)) {
    return joinMerkmalCsvIds(getMerkmalCurrentIds(ext));
  }
  if (isAttributeLikeField(ext) && ext.hasFixedOptions === true) {
    return attributeDisplayValue(ext);
  }
  return '';
};

/**
 * Compute the initial state for a single field on detail load. Drives the
 * left-column version-pill highlight, the right-column textarea seed, and the
 * dirty / useVersion flags that gate Save Draft / Apply.
 *
 * Rules - on entry the right column is filled and dirty ONLY when status
 * indicates there is an unapplied AI-generated draft waiting (i.e. a
 * non-current latest version exists). This covers two statuses today:
 *   - `IN_PROGRESS`: legacy per-field generation flow (user clicked Generate).
 *   - `BULK_GENERATE_DONE`: bulk generation worker finished - drafts live in
 *     `content_master_field_version` exactly like IN_PROGRESS. We want the
 *     same single-product Apply flow available from the product page.
 *
 * Everything else is rendered as a clean view:
 *
 *   - NEW (no versions): Original pill highlighted, right empty, nothing dirty.
 *   - DONE (has isCurrent): Current pill highlighted, right empty, nothing dirty.
 *     Behaves like the user just clicked the current pill - even if a leftover
 *     draft exists on the backend, it stays hidden until the user explicitly
 *     edits the textarea or picks a non-current version.
 *   - IN_PROGRESS / BULK_GENERATE_DONE + non-current latest version: Latest
 *     pill auto-picked, right shows latest.value, useVersion=true, dirty=true
 *     → Save sends `{ id }` and Apply pushes to Wawi.
 *   - IN_PROGRESS / BULK_GENERATE_DONE without a usable version (only an
 *     isCurrent one, or none): Falls through to the clean-view branch.
 *
 * The right column is NEVER seeded from `detail[field]` (the original Wawi
 * value) or from the saved draft on entry - both would mirror the left column
 * (or look like the AI had run on a "clean" product). Drafts are still saved
 * on the backend; they just don't render on entry per Anton's spec.
 */
const computeFieldInitState = (
  detail: ContentMasterProductView,
  fieldKey: string
): FieldInitState => {
  const versions = getActiveLanguageVersions(detail)[fieldKey] || [];
  const currentVersion = versions.find((item) => item.isCurrent);
  const latestVersion = [...versions].sort(
    (a, b) => getVersionTimestamp(b) - getVersionTimestamp(a)
  )[0];

  const hasPendingAiDrafts =
    detail.processingStatus === ContentMasterProductView.processingStatus.IN_PROGRESS ||
    detail.processingStatus === ContentMasterProductView.processingStatus.BULK_GENERATE_DONE;
  const latestId = latestVersion?.id;
  const currentId = currentVersion?.id;

  const pickLatestAsDraft =
    hasPendingAiDrafts &&
    Boolean(latestId) &&
    (!currentId || latestId !== currentId);

  let selectedId: string;
  if (pickLatestAsDraft && latestId) {
    selectedId = latestId;
  } else if (currentId) {
    selectedId = currentId;
  } else if (latestId) {
    selectedId = latestId;
  } else {
    // No real versions exist → highlight the synthetic Original pill (= "logical
    // current" for NEW products: shows the original Wawi value on the left).
    selectedId = ORIGINAL_VERSION_ID;
  }

  const seedValue =
    pickLatestAsDraft && typeof latestVersion?.value === 'string'
      ? latestVersion.value
      : computeNeutralSeed(detail, fieldKey);

  return {
    selectedId,
    useVersion: pickLatestAsDraft,
    dirty: pickLatestAsDraft,
    seedValue,
  };
};

const ContentMasterProductPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  // Subuser: generate / translate / save-draft require content_master_write;
  // Apply writes into Wawi and requires the separate content_master_wawi_write.
  const { can } = usePermissions();
  const canCmWrite = can('content_master_write');
  const canCmWawiWrite = can('content_master_wawi_write');
  const { editWawiAttributes } = useContentMasterEntitlements();
  const wawiFieldsLocked = !editWawiAttributes;
  const params = useParams<{ productId: string }>();
  /**
   * The list page passes `{ fromContentMasterList: true }` in router state when
   * it links into a product. We use it to choose between `navigate(-1)`
   * (correct for SPA-internal navigation: preserves scroll, list cache, and
   * the locally-patched status) and an explicit `/features/content-master`
   * URL (used when the user opened the product via direct URL / new tab -
   * `navigate(-1)` would otherwise leave our app entirely).
   * Captured once on mount so a transient `setSearchParams` later in the
   * lifecycle can't accidentally clear it.
   */
  const cameFromContentMasterListRef = useRef<boolean>(
    !!(location.state as { fromContentMasterList?: boolean } | null)?.fromContentMasterList
  );
  const contentMasterListReturnRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const st = location.state as Partial<ContentMasterListLocationState> | null | undefined;
    if (st?.contentMasterListReturn) {
      contentMasterListReturnRef.current = st.contentMasterListReturn;
    }
  }, [location.state]);

  const getContentMasterListReturnUrl = (): string | null => {
    const fromState = (
      location.state as Partial<ContentMasterListLocationState> | null | undefined
    )?.contentMasterListReturn;
    if (fromState) {
      return fromState;
    }
    return contentMasterListReturnRef.current;
  };

  const inProgressQueueLinkState = useMemo(
    () => ({
      ...(typeof location.state === 'object' && location.state !== null
        ? (location.state as Record<string, unknown>)
        : {}),
      fromContentMasterList: true as const,
    }),
    [location.state]
  );
  /**
   * Guards the one-shot "auto-pick best default language on entry" effect
   * (Content Master #180). We only redirect the URL once per `productId` so a
   * later user-initiated language switch through the language `<select>` is
   * never overridden - even when the user picks an empty language on purpose.
   * Reset whenever `productId` changes so opening another product re-applies
   * the rule.
   */
  const didAutoPickLanguageRef = useRef<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const sourceParam = searchParams.get('source');
  /**
   * Channel id taken straight from the URL. The backend expects the
   * composite format `^(plattform|shop):\d+$`; anything else (legacy
   * numeric kShop, hand-edited junk, stale `jtl`/`amazon`) is treated as
   * "missing" and the resolution effect below picks a sensible default
   * from `workspace-config.platforms` (URL > localStorage > master).
   */
  const sourceFromQuery: ContentMasterSource = isContentMasterSourceLiteral(sourceParam)
    ? sourceParam
    : '';
  const queryLanguage = searchParams.get('language') ?? '';
  /**
   * URL-driven raw value for Select C (Translate target). Independent from
   * `queryLanguage` (Selects A and B share state via `?language=`). The
   * `targetLanguage` is sent only in the `POST /translate` body and as the
   * `language` field of subsequent `save-draft` / `apply` while the user is
   * still in translate mode - it does NOT trigger a `GET /product` reload.
   */
  const queryTargetLanguage = searchParams.get('targetLanguage') ?? '';
  const languageForGet = queryLanguage.trim() || undefined;
  const preferDeUi = i18n.language.toLowerCase().startsWith('de');

  const productId = Number(params.productId);

  const {
    detail,
    detailLoading,
    detailError,
    generateAllLoading,
    generateFieldLoading,
    translateAllLoading,
    translateFieldLoading,
    saveDraftLoading,
    applyLoading,
    actionError,
    workspaceConfig,
    wawiLanguages,
    wawiLanguagesLoading,
    quotaExceededModalVisible,
  } = useAppSelector((state) => state.contentMaster);

  const generateSyncSubmitting = useAppSelector(
    (state) => state.marketplaceListing.generateSyncSubmitting
  );

  const dataSourceIdForApi = detail?.dataSourceId ?? workspaceConfig?.dataSourceId ?? null;

  /**
   * Marktplatz channel: the product has no editable core content - only the
   * category-attribute `marketplace` section (rendered below). Core
   * save-draft / apply / generate / translate are unsupported here (the
   * backend rejects core apply with 400 `content_master_marketplace_channel_unsupported`),
   * so we suppress the core editor actions for these channels.
   */
  const isMarketplaceChannel = Boolean(detail?.marketplace);

  // Mid-generation: another flow queued/started this product on the worker -
  // block the Generate button until the next reload reports it finished.
  const marketplaceGenerating =
    detail?.processingStatus === 'listing_generate_queued' ||
    detail?.processingStatus === 'listing_generate_in_progress';

  // Marketplace channel: Save/Apply are driven from the single, fixed page
  // action-bar but the edit state lives in MarketplaceFieldsSection. The section
  // exposes its flows via this handle and reports button state via onStateChange.
  const marketplaceEditorRef = useRef<MarketplaceFieldsSectionHandle | null>(null);
  const [marketplaceEditorState, setMarketplaceEditorState] = useState<MarketplaceEditorState>({
    canSave: false,
    canApply: false,
    saving: false,
    applying: false,
  });
  const [isMarketplaceGenerateModalOpen, setIsMarketplaceGenerateModalOpen] = useState(false);
  const handleMarketplaceEditorState = useCallback((next: MarketplaceEditorState) => {
    setMarketplaceEditorState((prev) =>
      prev.canSave === next.canSave &&
      prev.canApply === next.canApply &&
      prev.saving === next.saving &&
      prev.applying === next.applying
        ? prev
        : next
    );
  }, []);

  const channelOptionsFlat = useMemo(
    () => buildContentMasterChannelOptions(workspaceConfig?.platforms),
    [workspaceConfig?.platforms]
  );
  const channelGroups = useMemo(
    () => buildContentMasterChannelGroups(workspaceConfig?.platforms),
    [workspaceConfig?.platforms]
  );
  const availableChannelIds = useMemo(
    () => channelOptionsFlat.map((o) => o.value),
    [channelOptionsFlat]
  );
  const resolveSourceLabel = useCallback(
    (channelSource: string): string =>
      resolveContentMasterSourceLabel(channelOptionsFlat, channelSource),
    [channelOptionsFlat]
  );
  const showChannelSelect = channelOptionsFlat.length > 1;

  const source: ContentMasterSource = useMemo(() => {
    if (!availableChannelIds.length) {
      return sourceFromQuery;
    }
    if (sourceFromQuery && availableChannelIds.includes(sourceFromQuery)) {
      return sourceFromQuery;
    }
    const persisted = readLastSelectedChannelId(workspaceConfig?.dataSourceId);
    if (persisted && availableChannelIds.includes(persisted)) {
      return persisted;
    }
    return (
      pickDefaultContentMasterChannelId(workspaceConfig?.platforms) ??
      availableChannelIds[0]
    );
  }, [
    availableChannelIds,
    sourceFromQuery,
    workspaceConfig?.dataSourceId,
    workspaceConfig?.platforms,
  ]);

  const inProgressLocalPageCacheRef = useRef<{
    key: string;
    pages: Map<number, ContentMasterLocalProductItemView[]>;
    total: number | null;
  }>({ key: '', pages: new Map(), total: null });

  const fetchRawLocalInProgressPage = useCallback(
    async (page: number) => {
      if (!source) {
        return null;
      }
      return ContentMasterService.getAppContentMasterProductsLocalByStatusList(
        ContentMasterProductView.processingStatus.IN_PROGRESS,
        source,
        dataSourceIdForApi?.trim() || undefined,
        page,
        IN_PROGRESS_LOCAL_QUEUE_LIMIT
      );
    },
    [dataSourceIdForApi, source]
  );

  const loadLocalInProgressQueuePage = useCallback(
    async (page: number) => {
      if (!source) {
        return null;
      }
      const ds = dataSourceIdForApi?.trim() ?? '';
      const key = `${source}\u0000${ds}`;
      const bucket = inProgressLocalPageCacheRef.current;
      if (bucket.key !== key) {
        bucket.key = key;
        bucket.pages = new Map();
        bucket.total = null;
      }
      const cached = bucket.pages.get(page);
      if (cached) {
        return {
          items: cached,
          total: bucket.total ?? undefined,
          page,
          limit: IN_PROGRESS_LOCAL_QUEUE_LIMIT,
        };
      }
      const res = await fetchRawLocalInProgressPage(page);
      if (!res) {
        return null;
      }
      bucket.pages.set(page, res.items ?? []);
      if (typeof res.total === 'number') {
        bucket.total = res.total;
      }
      return res;
    },
    [dataSourceIdForApi, fetchRawLocalInProgressPage, source]
  );

  useEffect(() => {
    if (!availableChannelIds.length) {
      return;
    }
    if (source && sourceFromQuery !== source) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('source', source);
          return next;
        },
        { replace: true }
      );
    }
  }, [availableChannelIds, source, sourceFromQuery, setSearchParams]);

  const postLanguage: ContentMasterPostLanguage = useMemo(() => {
    const fromUrl = queryLanguage.trim();
    if (fromUrl) {
      return fromUrl;
    }
    return pickDefaultWawiLanguageCode(wawiLanguages, workspaceConfig?.defaultLanguage) || 'de';
  }, [queryLanguage, wawiLanguages, workspaceConfig?.defaultLanguage]);

  const languageSelectValue = useMemo(() => {
    const raw = queryLanguage.trim();
    if (!raw) {
      return '';
    }
    if (!wawiLanguages.length) {
      return raw;
    }
    return isWawiLanguageCodeAllowed(raw, wawiLanguages) ? raw : '';
  }, [queryLanguage, wawiLanguages]);

  /**
   * Resolved value for Select C - the active translation target. Falls back to
   * the first non-source active shop language so the Translate buttons aren't
   * disabled by default on a multi-language workspace. If the workspace only
   * has one active language, target == source and we expose the conflict via
   * the `pageLanguage === targetLanguage` guards below.
   */
  const targetLanguage = useMemo<ContentMasterPostLanguage>(() => {
    const raw = queryTargetLanguage.trim();
    if (raw && (wawiLanguages.length === 0 || isWawiLanguageCodeAllowed(raw, wawiLanguages))) {
      return raw;
    }
    if (wawiLanguages.length === 0) {
      return postLanguage;
    }
    const firstNonSource = wawiLanguages
      .map((l) => wawiLanguageToApiValue(l))
      .find((code) => code && code !== postLanguage);
    return firstNonSource || postLanguage;
  }, [queryTargetLanguage, wawiLanguages, postLanguage]);

  const [editorMode, setEditorMode] = useState<ContentMasterEditorMode>('generate');

  const [editableValues, setEditableValues] = useState<FieldValues>(createEmptyFieldValues());
  const [wawiExtValues, setWawiExtValues] = useState<Record<string, string>>({});
  const [selectedVersionIds, setSelectedVersionIds] = useState<SelectedVersionMap>({});
  const [useVersionSelection, setUseVersionSelection] = useState<VersionSelectionMap>({});
  const [dirtyFields, setDirtyFields] = useState<DirtyFieldsMap>({});
  /**
   * Snapshot of every field's initial seed value (taken right after `detail`
   * loads / changes). Used by `setDirtyFromUserEdit` to decide whether a
   * textarea / input / select edit is genuinely a change or has just been
   * reverted to the original - without this, typing then deleting back to the
   * seed value would still mark the field dirty, leaving Save / Apply enabled
   * for a no-op edit (Yuliya bug #179).
   */
  const [initialFieldValues, setInitialFieldValues] = useState<Record<string, string>>({});
  /** Checked API field names included in POST /content-master/generate `fields` (non-empty required). */
  const [generateBatchSelection, setGenerateBatchSelection] = useState<Record<string, boolean>>({});
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  /**
   * "Additional product information" of the generate modal ("Generate all"
   * only - per-field generation deliberately sends NO context). Lives at page
   * level so it survives closing/reopening the modal: the context lives one
   * REQUEST and is never stored on the server, so the user may re-generate
   * with the same context. Sent as `additionalContext` when non-empty.
   */
  const [generateAdditionalContext, setGenerateAdditionalContext] =
    useState<AdditionalContextState>(emptyAdditionalContextState);
  const [applyWarningOpen, setApplyWarningOpen] = useState(false);
  /**
   * Checked API field names for POST /content-master/translate `fields[]`
   * (non-empty required). Only translatable keys are tracked here - fields the
   * backend silently skips (Merkmal, non-varchar wawiAttr) are NOT in the
   * selection map and render as disabled-with-tooltip in the picker.
   */
  const [translateBatchSelection, setTranslateBatchSelection] = useState<Record<string, boolean>>({});
  const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);
  /** Shared HTML preview modal - same UX as the bulk review page. */
  const { open: openHtmlPreview, modal: htmlPreviewModal } = useHtmlPreview();

  const [inProgressQueue, setInProgressQueue] = useState<{
    total: number;
    position: number;
  } | null>(null);
  const [inProgressQueueLoading, setInProgressQueueLoading] = useState(false);
  const [inProgressAdjacentNav, setInProgressAdjacentNav] = useState<{
    prevTo: string | null;
    nextTo: string | null;
  }>({ prevTo: null, nextTo: null });
  const inProgressQueueSyncSeqRef = useRef(0);

  const extensionKeys = useMemo(
    () => collectExtendedFieldKeys(detail?.wawiExtendedFields),
    [detail?.wawiExtendedFields]
  );

  const isActionLoading =
    generateAllLoading || translateAllLoading || saveDraftLoading || applyLoading;
  const blockActionsWhileDetailLoading = isActionLoading || detailLoading;

  /**
   * Single source of truth for "Translate buttons are unusable right now".
   * Mirrors backend rules:
   *   - sourceLanguage and targetLanguage MUST differ (`content_master_invalid_language`),
   *   - both must be active shop languages.
   * When `wawiLanguages` hasn't loaded yet we don't enforce the active-shop
   * check (we'd otherwise grey-out the buttons during initial mount).
   */
  const translateDisabledReason = useMemo<string | null>(() => {
    if (!postLanguage) {
      return 'no_source';
    }
    if (!targetLanguage) {
      return 'no_target';
    }
    if (postLanguage === targetLanguage) {
      return 'same_language';
    }
    if (wawiLanguages.length > 0) {
      if (!isWawiLanguageCodeAllowed(postLanguage, wawiLanguages)) {
        return 'invalid_source';
      }
      if (!isWawiLanguageCodeAllowed(targetLanguage, wawiLanguages)) {
        return 'invalid_target';
      }
    }
    return null;
  }, [postLanguage, targetLanguage, wawiLanguages]);

  const canTranslate = translateDisabledReason === null;

  /**
   * Compare a new user-supplied value for a field with its initial seed and
   * flip `dirtyFields[fieldKey]` accordingly. Keeps Save/Apply correctly
   * disabled when the user types something and then reverts it (e.g. types
   * into an empty AI-text field and deletes everything back) - Yuliya bug
   * #179. NOT used by version-pill clicks (which set dirty explicitly per
   * their own semantics) nor by Generate handlers (a fresh AI text is always
   * a real change vs the seed).
   */
  const setDirtyFromUserEdit = (fieldKey: string, newValue: string) => {
    const original = initialFieldValues[fieldKey] ?? '';
    setDirtyFields((prev) => {
      const wasDirty = !!prev[fieldKey];
      const shouldBeDirty = newValue !== original;
      if (wasDirty === shouldBeDirty) {
        return prev;
      }
      return { ...prev, [fieldKey]: shouldBeDirty };
    });
  };

  const setChannelInUrl = (nextSource: ContentMasterSource) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('source', nextSource);
        return next;
      },
      { replace: true }
    );
  };

  const setLanguageInUrl = (nextLanguage: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const trimmed = nextLanguage.trim();
        if (!trimmed) {
          next.delete('language');
        } else {
          next.set('language', trimmed);
        }
        return next;
      },
      { replace: true }
    );
  };

  /**
   * Counterpart to `setLanguageInUrl` for Select C. Intentionally NOT bundled
   * into the same effect that calls `fetchContentMasterProductDetail`: target
   * language flips affect only future `POST /translate` / `save-draft` /
   * `apply` requests, not the cached product detail. Keeping the URL update
   * isolated avoids redundant detail fetches when the user only changes the
   * target language.
   */
  const setTargetLanguageInUrl = (nextLanguage: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const trimmed = nextLanguage.trim();
        if (!trimmed) {
          next.delete('targetLanguage');
        } else {
          next.set('targetLanguage', trimmed);
        }
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (!inProgressQueue || !source) {
      setInProgressAdjacentNav({ prevTo: null, nextTo: null });
      return;
    }
    let cancelled = false;

    const pathForQueuePosition = async (pos: number): Promise<string | null> => {
      const { total } = inProgressQueue;
      if (pos < 1 || pos > total) {
        return null;
      }
      const pageNum = Math.floor((pos - 1) / IN_PROGRESS_LOCAL_QUEUE_LIMIT) + 1;
      const idx = (pos - 1) % IN_PROGRESS_LOCAL_QUEUE_LIMIT;
      const res = await loadLocalInProgressQueuePage(pageNum);
      if (!res) {
        return null;
      }
      const item = res.items?.[idx];
      const wid = item?.wawiArtikelId;
      if (typeof wid !== 'number' || !Number.isFinite(wid) || wid <= 0) {
        return null;
      }
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(IN_PROGRESS_QUEUE_URL_PARAM, String(pos));
      const langFromItem = item?.language?.trim();
      if (langFromItem) {
        nextParams.set('language', langFromItem);
      }
      const qs = nextParams.toString();
      return `/features/content-master/product/${wid}${qs ? `?${qs}` : ''}`;
    };

    const run = async () => {
      const { position } = inProgressQueue;
      const [prevTo, nextTo] = await Promise.all([
        pathForQueuePosition(position - 1),
        pathForQueuePosition(position + 1),
      ]);
      if (!cancelled) {
        setInProgressAdjacentNav({ prevTo, nextTo });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [inProgressQueue, loadLocalInProgressQueuePage, searchParams, source]);

  /**
   * Resolves the human-readable suffix for the left column header
   * ("Original Content (EN) (Read-only)" in the design). Uses the active
   * `pageLanguage` (= what GET /product was called with) and tries to format
   * it as the language's ISO2 in upper case for parity with the design; falls
   * back to whatever value is in `pageLanguage` when the workspace languages
   * haven't loaded yet.
   */
  const pageLanguageDisplay = useMemo<string>(() => {
    const raw = postLanguage;
    if (!raw) {
      return '';
    }
    const match = wawiLanguages.find((l) => wawiLanguageToApiValue(l) === raw);
    if (match?.iso2) {
      return match.iso2.toUpperCase();
    }
    return raw.toUpperCase();
  }, [postLanguage, wawiLanguages]);

  /**
   * Format the displayed text + tooltip of a language `<option>` based on the
   * cross-language draft state derived from `detail.languageVersions`
   * (Content Master #180). Empty languages render with no marker so the
   * default "no work yet" case stays visually quiet; languages with unsaved
   * drafts get a `●` prefix and ones already pushed to Wawi get a `✓`.
   *
   * Implemented as plain text inside the option (not styled HTML) because
   * `<select>` only renders option text - colored markers via inline styles
   * are unreliable across browsers / OS native dropdowns.
   */
  const formatLanguageOptionLabel = (
    code: string,
    label: string
  ): { text: string; title: string | undefined } => {
    const state: ContentMasterLanguageState = getContentMasterLanguageState(detail, code);
    if (state === 'draft') {
      return {
        text: `${label} ●`,
        title: t('content_master_language_state_draft'),
      };
    }
    if (state === 'applied') {
      return {
        text: `${label} ✓`,
        title: t('content_master_language_state_applied'),
      };
    }
    return { text: label, title: undefined };
  };

  /**
   * "Some OTHER language has unsaved work" flag (Content Master #180). Drives
   * the orange dot rendered next to the "Language" / "Source Language" /
   * "Target Language" select labels. We intentionally exclude the currently
   * selected `postLanguage` from the check - a draft in the language the
   * user is already looking at is signalled by the per-field dirty markers
   * inside the editor; the label-level badge is reserved for "you have work
   * elsewhere, consider switching". Computed across the active shop
   * languages from `wawiLanguages` so we don't flag stale codes that no
   * longer belong to the workspace.
   */
  const hasOtherLanguageDraft = useMemo<boolean>(() => {
    if (!detail) {
      return false;
    }
    return wawiLanguages.some((lang) => {
      const code = wawiLanguageToApiValue(lang);
      if (!code || code === postLanguage) {
        return false;
      }
      return getContentMasterLanguageState(detail, code) === 'draft';
    });
  }, [detail, wawiLanguages, postLanguage]);

  // Refetch workspace-config only when the loaded product's `dataSourceId`
  // differs from the one the cached snapshot was built for (Wawi switch /
  // multi-DB users). Otherwise the bootstrap-loaded snapshot is reused - no
  // duplicate request.
  useEffect(() => {
    const detailDsId = detail?.dataSourceId;
    if (!detailDsId) {
      return;
    }
    const cachedDsId = workspaceConfig?.dataSourceId ?? null;
    if (cachedDsId === detailDsId) {
      return;
    }
    dispatch(fetchContentMasterWorkspaceConfig(detailDsId));
  }, [dispatch, detail?.dataSourceId, workspaceConfig?.dataSourceId]);

  useEffect(() => {
    const raw = queryLanguage.trim();
    if (!raw || !wawiLanguages.length) {
      return;
    }
    if (!isWawiLanguageCodeAllowed(raw, wawiLanguages)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('language');
          return next;
        },
        { replace: true }
      );
    }
  }, [queryLanguage, setSearchParams, wawiLanguages]);

  useEffect(() => {
    const raw = queryTargetLanguage.trim();
    if (!raw || !wawiLanguages.length) {
      return;
    }
    if (!isWawiLanguageCodeAllowed(raw, wawiLanguages)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('targetLanguage');
          return next;
        },
        { replace: true }
      );
    }
  }, [queryTargetLanguage, setSearchParams, wawiLanguages]);

  useEffect(() => {
    if (!Number.isFinite(productId) || productId <= 0) {
      return;
    }
    setEditableValues(createEmptyFieldValues());
    setWawiExtValues({});
    setSelectedVersionIds({});
    setUseVersionSelection({});
    setDirtyFields({});
    // Drop the snapshot here too - the seeding effect will re-set it when
    // the new detail arrives. Until then, `beforeunload` short-circuits.
    formSnapshotRef.current = null;
    dispatch(clearContentMasterDetail());
    // Workspace config is owned by the centralized bootstrap. We just read
    // the cached `dataSourceId` (if any) and fire the detail request - the
    // dataSourceId-mismatch effect above re-syncs workspace-config when the
    // loaded product points at a different DB.
    const dsId = store.getState().contentMaster.workspaceConfig?.dataSourceId ?? undefined;
    dispatch(
      fetchContentMasterProductDetail({
        wawiArtikelId: productId,
        source,
        language: languageForGet,
        dataSourceId: dsId,
      })
    );
  }, [dispatch, languageForGet, productId, source]);

  useEffect(() => {
    if (!Number.isFinite(productId) || productId <= 0) {
      setInProgressQueue(null);
      setInProgressQueueLoading(false);
      return;
    }
    if (!source) {
      setInProgressQueue(null);
      setInProgressQueueLoading(false);
      return;
    }

    const detailForThisProduct =
      detail &&
      typeof detail.wawiArtikelId === 'number' &&
      detail.wawiArtikelId === productId;

    if (
      detailForThisProduct &&
      detail.processingStatus !== ContentMasterProductView.processingStatus.IN_PROGRESS
    ) {
      inProgressQueueSyncSeqRef.current += 1;
      setInProgressQueue(null);
      setInProgressQueueLoading(false);
      if (searchParams.get(IN_PROGRESS_QUEUE_URL_PARAM)) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(IN_PROGRESS_QUEUE_URL_PARAM);
            return next;
          },
          { replace: true }
        );
      }
      return;
    }

    const seq = ++inProgressQueueSyncSeqRef.current;
    let cancelled = false;

    const sync = async () => {
      setInProgressQueueLoading(true);
      try {
        const posFromUrl = ((): number | null => {
          const raw = searchParams.get(IN_PROGRESS_QUEUE_URL_PARAM);
          if (!raw) {
            return null;
          }
          const n = Number(raw);
          return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
        })();

        const tryConfirmUrlPosition = async (
          pos: number
        ): Promise<{ total: number; position: number } | null> => {
          const pageNum = Math.floor((pos - 1) / IN_PROGRESS_LOCAL_QUEUE_LIMIT) + 1;
          const res = await loadLocalInProgressQueuePage(pageNum);
          if (!res || cancelled || seq !== inProgressQueueSyncSeqRef.current) {
            return null;
          }
          const total = res.total ?? 0;
          if (pos > total || total === 0) {
            return null;
          }
          const idx = (pos - 1) % IN_PROGRESS_LOCAL_QUEUE_LIMIT;
          const item = res.items?.[idx];
          if (item?.wawiArtikelId === productId) {
            return { total, position: pos };
          }
          return null;
        };

        if (posFromUrl != null) {
          const confirmed = await tryConfirmUrlPosition(posFromUrl);
          if (confirmed) {
            if (seq !== inProgressQueueSyncSeqRef.current || cancelled) {
              return;
            }
            setInProgressQueue(confirmed);
            return;
          }
        }

        let pageNum = 1;
        let lastTotal = 0;
        while (!cancelled && seq === inProgressQueueSyncSeqRef.current) {
          const res = await loadLocalInProgressQueuePage(pageNum);
          if (!res || cancelled || seq !== inProgressQueueSyncSeqRef.current) {
            return;
          }
          const items = res.items ?? [];
          lastTotal = res.total ?? 0;
          const foundIdx = items.findIndex((i) => i.wawiArtikelId === productId);
          if (foundIdx >= 0) {
            const position =
              (pageNum - 1) * IN_PROGRESS_LOCAL_QUEUE_LIMIT + foundIdx + 1;
            if (seq !== inProgressQueueSyncSeqRef.current || cancelled) {
              return;
            }
            setInProgressQueue({ total: lastTotal, position });
            if (posFromUrl !== position) {
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set(IN_PROGRESS_QUEUE_URL_PARAM, String(position));
                  return next;
                },
                { replace: true }
              );
            }
            return;
          }
          if (
            lastTotal === 0 ||
            pageNum * IN_PROGRESS_LOCAL_QUEUE_LIMIT >= lastTotal ||
            items.length === 0
          ) {
            break;
          }
          pageNum += 1;
        }

        if (seq !== inProgressQueueSyncSeqRef.current || cancelled) {
          return;
        }
        setInProgressQueue(null);
        if (searchParams.get(IN_PROGRESS_QUEUE_URL_PARAM)) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete(IN_PROGRESS_QUEUE_URL_PARAM);
              return next;
            },
            { replace: true }
          );
        }
      } finally {
        if (seq === inProgressQueueSyncSeqRef.current) {
          setInProgressQueueLoading(false);
        }
      }
    };

    void sync();
    return () => {
      cancelled = true;
    };
  }, [detail, loadLocalInProgressQueuePage, productId, searchParams, setSearchParams, source]);

  /**
   * Auto-pick the most useful language to open on first detail load (CM #180):
   * if the language the page resolved to has no stored versions but another
   * language has unsaved drafts (or applied content), redirect the URL there
   * so the user lands on the language with actual work instead of an empty
   * shop default. Runs at most once per `productId` to avoid fighting any
   * user-initiated language switch in the `<select>`. Requires `wawiLanguages`
   * already loaded so the candidate list is correct.
   */
  useEffect(() => {
    if (!Number.isFinite(productId) || productId <= 0) {
      return;
    }
    if (didAutoPickLanguageRef.current === productId) {
      return;
    }
    if (!detail) {
      return;
    }
    if (!wawiLanguages.length) {
      return;
    }
    didAutoPickLanguageRef.current = productId;

    const candidateLanguages = wawiLanguages
      .map((lang) => wawiLanguageToApiValue(lang))
      .filter((code): code is string => code.length > 0);
    const workspaceDefault =
      pickDefaultWawiLanguageCode(wawiLanguages, workspaceConfig?.defaultLanguage) ||
      postLanguage;
    const next = pickContentMasterDefaultLanguage(
      detail,
      candidateLanguages,
      postLanguage,
      workspaceDefault
    );
    if (next && next !== postLanguage) {
      setLanguageInUrl(next);
    }
    // We intentionally do NOT include `setLanguageInUrl` in the dependency list
    // - it's a stable closure over `setSearchParams` and listing it would force
    // the rule "exhaustive-deps" without changing behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, wawiLanguages, postLanguage, productId, workspaceConfig?.defaultLanguage]);

  useEffect(() => {
    dispatch(clearAllNotifications());
    return () => {
      dispatch(clearContentMasterDetail());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!detail) {
      setGenerateBatchSelection({});
      setTranslateBatchSelection({});
      return;
    }

    const nextValues = createEmptyFieldValues();
    const nextWawi: Record<string, string> = {};
    const nextSelected: SelectedVersionMap = {};
    const nextVersionSelection: VersionSelectionMap = {};
    const nextDirtyFields: DirtyFieldsMap = {};
    const nextInitial: Record<string, string> = {};

    // Per-field init driven by `computeFieldInitState` - see its docstring for the
    // status × draft × versions matrix. The right column is never seeded from the
    // original Wawi value (it would mirror the left column and falsely suggest the
    // AI had run).
    editableFields.forEach((field) => {
      const init = computeFieldInitState(detail, field);
      nextValues[field] = init.seedValue;
      nextSelected[field] = init.selectedId;
      nextVersionSelection[field] = init.useVersion;
      nextDirtyFields[field] = init.dirty;
      nextInitial[field] = init.seedValue;
    });

    const extKeys = collectExtendedFieldKeys(detail.wawiExtendedFields);
    extKeys.forEach((key) => {
      const init = computeFieldInitState(detail, key);
      nextWawi[key] = init.seedValue;
      nextSelected[key] = init.selectedId;
      nextVersionSelection[key] = init.useVersion;
      nextDirtyFields[key] = init.dirty;
      nextInitial[key] = init.seedValue;
    });

    setEditableValues(nextValues);
    setWawiExtValues(nextWawi);
    setSelectedVersionIds(nextSelected);
    setUseVersionSelection(nextVersionSelection);
    setDirtyFields(nextDirtyFields);
    setInitialFieldValues(nextInitial);
    // Capture the navigation-guard snapshot from the SAME values being
    // applied above. Doing it here (before React commits the setStates)
    // means the snapshot can never disagree with the state it represents,
    // even across React 18 batching boundaries.
    formSnapshotRef.current = JSON.stringify({
      e: nextValues,
      w: nextWawi,
      s: nextSelected,
      u: nextVersionSelection,
    });
    // Pin the URL that "owns" this snapshot so the popstate handler can
    // restore the address bar when intercepting a browser-back.
    snapshotUrlRef.current = window.location.href;

    // Fields the backend marks as opt-in only (today `["urlPath"]` - regenerating
    // a live shop URL must be an explicit user decision). They stay in the
    // picker but start unchecked; "select all" skips them too.
    const generateExcluded = new Set(detail.generateDefaultExcludedFields ?? []);
    const nextBatch: Record<string, boolean> = {};
    CONTENT_MASTER_CORE_FIELD_KEYS.forEach((k) => {
      nextBatch[k] = !generateExcluded.has(k);
    });
    for (const f of detail.wawiExtendedFields ?? []) {
      if (!f.fieldApiName || wawiFieldsLocked) {
        continue;
      }
      if (isAttributeLikeField(f) && canAiGenerateAttributeLike(f)) {
        nextBatch[f.fieldApiName] = !generateExcluded.has(f.fieldApiName);
        continue;
      }
      if (isMerkmalField(f) && canAiGenerateMerkmal(f)) {
        nextBatch[f.fieldApiName] = !generateExcluded.has(f.fieldApiName);
      }
    }
    setGenerateBatchSelection(nextBatch);

    // Translate selection mirrors the same "translatable keys" set the backend
    // accepts: core fields + varchar (non-readonly) attribut/eigene. Merkmale
    // and non-varchar attrs are excluded from the map entirely so they stay
    // unselectable in the picker (rendered as disabled rows for transparency).
    // Pre-selection: core fields where the source-language value is non-empty
    // (per spec). Varchar attrs default to unchecked so the user opts in.
    // `generateDefaultExcludedFields` (e.g. urlPath) start unchecked here too.
    const nextTranslateBatch: Record<string, boolean> = {};
    CONTENT_MASTER_CORE_FIELD_KEYS.forEach((k) => {
      const sourceValue = detail[k];
      nextTranslateBatch[k] =
        !generateExcluded.has(k) &&
        typeof sourceValue === 'string' &&
        sourceValue.length > 0;
    });
    for (const f of detail.wawiExtendedFields ?? []) {
      if (!f.fieldApiName) {
        continue;
      }
      if (canAiGenerateAttributeLike(f)) {
        nextTranslateBatch[f.fieldApiName] = false;
      }
    }
    setTranslateBatchSelection(nextTranslateBatch);
  }, [detail, wawiFieldsLocked]);

  useEffect(() => {
    if (!detailError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: detailError,
      })
    );
  }, [detailError, dispatch, t]);

  useEffect(() => {
    if (!actionError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: actionError,
      })
    );
  }, [actionError, dispatch, t]);

  const productName = useMemo(() => detail?.title || `#${productId}`, [detail?.title, productId]);

  const reloadDetail = () => {
    if (!Number.isFinite(productId) || productId <= 0) {
      return;
    }
    dispatch(
      fetchContentMasterProductDetail({
        wawiArtikelId: productId,
        source,
        language: languageForGet,
        dataSourceId: dataSourceIdForApi ?? undefined,
      })
    );
  };

  /**
   * Confirm handler for the marketplace-listing generate modal (single-product
   * variant). Fires the SYNCHRONOUS `POST /marketplace-listing/generate-sync`
   * and reflects the final status from the response: the generated values are
   * pulled by `reloadDetail()` (GET /content-master/product → draft). The
   * response carries only status/summary, so on `generated` we reload; on the
   * other `generationStatus` codes / `listing_generate_failed` / 409 we toast.
   */
  const handleConfirmMarketplaceGenerateSync = async ({
    language,
    marketplace,
    generateRequired,
    generateRecommended,
    generateOther,
    generatePrefilled,
    onlyEmptyFields,
    autoDetectCategory,
    listingItems,
    additionalContext,
  }: BulkGenerateConfirmParams) => {
    if (!detail || !source) {
      return;
    }
    // The single product is dropped by the modal's category gate when it has no
    // category and auto-detect is off - nothing to generate in that case.
    if (listingItems && listingItems.length === 0) {
      dispatch(
        addNotification({
          type: 'warning',
          title: t('notification_warning'),
          message: t('marketplace_listing_submit_all_skipped'),
        })
      );
      return;
    }
    try {
      const result = await dispatch(
        submitMarketplaceListingGenerateSync({
          source,
          language,
          wawiArtikelId: productId,
          marketplace,
          generateRequired,
          generateRecommended,
          generateOther,
          generatePrefilled,
          onlyEmptyFields,
          autoDetectCategory,
          dataSourceId: dataSourceIdForApi ?? null,
          additionalContext,
        })
      ).unwrap();
      setIsMarketplaceGenerateModalOpen(false);
      if (result.processingStatus === 'listing_generate_failed') {
        dispatch(
          addNotification({
            type: 'error',
            title: t('error'),
            message: result.errorMessage || t('marketplace_listing_generate_sync_failed'),
          })
        );
      } else if (result.generationStatus === 'generated' && result.summary?.requestedTotal === 0) {
        // "Fill only empty fields" with nothing left to fill: a regular done,
        // not an error - the product was skipped without an AI call.
        dispatch(
          addNotification({
            type: 'info',
            title: t('notification_info'),
            message: t('marketplace_listing_generate_sync_nothing_to_fill'),
          })
        );
      } else if (result.generationStatus === 'generated') {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('marketplace_listing_generate_sync_success'),
          })
        );
      } else if (result.generationStatus === 'category_not_selected') {
        dispatch(
          addNotification({
            type: 'warning',
            title: t('notification_warning'),
            message: t('marketplace_listing_gen_category_not_selected'),
          })
        );
      } else if (result.generationStatus === 'category_attributes_missing') {
        dispatch(
          addNotification({
            type: 'warning',
            title: t('notification_warning'),
            message: t('marketplace_listing_gen_category_attributes_missing'),
          })
        );
      } else if (result.generationStatus === 'no_target_attributes') {
        dispatch(
          addNotification({
            type: 'info',
            title: t('notification_info'),
            message: t('marketplace_listing_gen_no_target_attributes'),
          })
        );
      }
      // Reload so the generated draft (or the changed status) is reflected.
      reloadDetail();
    } catch (err) {
      const errorCode =
        err && typeof err === 'object' && 'errorCode' in err
          ? (err as { errorCode?: string | null }).errorCode
          : null;
      // 403 pre-flight rejections open the shared Content Master modals -
      // same contract as the bulk generate flows.
      if (errorCode === 'content_master_quota_exceeded') {
        dispatch(setContentMasterQuotaModalVisible(true));
        setIsMarketplaceGenerateModalOpen(false);
        return;
      }
      if (errorCode === 'content_master_subscription_insufficient') {
        dispatch(setContentMasterSubscriptionInsufficientModalVisible(true));
        setIsMarketplaceGenerateModalOpen(false);
        return;
      }
      if (errorCode === 'already_in_progress') {
        dispatch(
          addNotification({
            type: 'warning',
            title: t('notification_warning'),
            message: t('marketplace_listing_generate_sync_in_progress'),
          })
        );
        setIsMarketplaceGenerateModalOpen(false);
        reloadDetail();
        return;
      }
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message?: string }).message
          : undefined;
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: message || t('marketplace_listing_generate_sync_failed'),
        })
      );
    }
  };

  const selectedGenerateFieldKeys = useMemo(
    () => Object.entries(generateBatchSelection).filter(([, on]) => on).map(([k]) => k),
    [generateBatchSelection]
  );

  const selectedTranslateFieldKeys = useMemo(
    () => Object.entries(translateBatchSelection).filter(([, on]) => on).map(([k]) => k),
    [translateBatchSelection]
  );

  // Token estimates for the field-picker modals: config formula
  // (base + N × perField) × tier over the selected field count - same
  // calculation as the server pre-flight (one operation per modal).
  const generateModalTokenEstimate = useMemo(
    () =>
      estimateContentMasterAiTokens(
        workspaceConfig?.publicConfigs,
        selectedGenerateFieldKeys.length,
        selectedGenerateFieldKeys.length > 0 ? 1 : 0,
        'generate'
      ),
    [workspaceConfig?.publicConfigs, selectedGenerateFieldKeys.length]
  );

  const translateModalTokenEstimate = useMemo(
    () =>
      estimateContentMasterAiTokens(
        workspaceConfig?.publicConfigs,
        selectedTranslateFieldKeys.length,
        selectedTranslateFieldKeys.length > 0 ? 1 : 0,
        'translate'
      ),
    [workspaceConfig?.publicConfigs, selectedTranslateFieldKeys.length]
  );

  const openGenerateModal = () => {
    if (!detail) {
      return;
    }
    setIsGenerateModalOpen(true);
  };

  const closeGenerateModal = () => {
    setIsGenerateModalOpen(false);
  };

  const openTranslateModal = () => {
    if (!detail || !canTranslate) {
      return;
    }
    setIsTranslateModalOpen(true);
  };

  const closeTranslateModal = () => {
    setIsTranslateModalOpen(false);
  };

  /**
   * API field names a "select all" action must NOT tick (opt-in only) - from
   * `GET /content-master/product.generateDefaultExcludedFields` (today
   * `["urlPath"]`, but the list is backend-driven - never hardcode the names).
   * The user can still tick these fields manually.
   */
  const generateDefaultExcluded = useMemo(
    () => new Set(detail?.generateDefaultExcludedFields ?? []),
    [detail?.generateDefaultExcludedFields]
  );

  const setBatchTranslateField = (fieldKey: string, checked: boolean) => {
    setTranslateBatchSelection((prev) => ({ ...prev, [fieldKey]: checked }));
  };

  /**
   * Bulk toggle for the translate picker. Operates only on keys already
   * present in `translateBatchSelection` - i.e. translatable fields. Disabled
   * rows (Merkmal, non-varchar attrs) are not in the map and stay unaffected.
   */
  const selectAllTranslatableBatchFields = (checked: boolean) => {
    setTranslateBatchSelection((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((k) => {
        // "Select all" restores the default selection - it never ticks the
        // backend-excluded opt-in fields (e.g. urlPath).
        next[k] = checked && !generateDefaultExcluded.has(k);
      });
      return next;
    });
  };

  // Composed additional context + submit blockers (over the 50k limit / a
  // file still parsing) for the generate modal.
  const generateAdditionalContextText = buildAdditionalContext(generateAdditionalContext);
  const generateAdditionalContextTooLong =
    generateAdditionalContextText.length > ADDITIONAL_CONTEXT_MAX_LENGTH;
  const generateAdditionalContextParsing = hasParsingAdditionalContextFiles(
    generateAdditionalContext
  );
  // Token surcharge for the context (UI projection of the server formula).
  // Field count mirrors the base modal estimate (`generateModalTokenEstimate`);
  // 0 for an empty context, null when the rate config key is missing.
  const generateAdditionalContextSurcharge = estimateAdditionalContextTokens(
    workspaceConfig?.publicConfigs,
    generateAdditionalContextText.trim().length,
    selectedGenerateFieldKeys.length
  );

  const handleGenerateAll = async () => {
    if (!detail || generateAdditionalContextTooLong || generateAdditionalContextParsing) {
      return;
    }
    const fields = filterGenerateFieldsForEntitlements(selectedGenerateFieldKeys, {
      editWawiAttributes,
      bulkGenerationIncluded: true,
    });
    if (fields.length === 0) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('content_master_generate_select_at_least_one'),
        })
      );
      return;
    }

    const fieldAllowedValues = buildFieldAllowedValuesForGenerate(detail, fields);

    const result = await dispatch(
      generateAllContentMasterFields({
        wawiArtikelId: productId,
        source,
        language: postLanguage,
        dataSourceId: dataSourceIdForApi,
        fields,
        fieldAllowedValues,
        // Empty context → field omitted from the request.
        additionalContext:
          generateAdditionalContextText.trim().length > 0
            ? generateAdditionalContextText
            : undefined,
      })
    );

    if (!generateAllContentMasterFields.fulfilled.match(result)) {
      return;
    }

    // Drop fields the AI returned EMPTY (e.g. a picklist attribute where no
    // allowed option fits): they must never reach save-draft/apply - an empty
    // `text` now CLEARS the Wawi value, which is reserved for an explicit user
    // edit. Skipping them here keeps the field non-dirty, so buildPayloadFields
    // excludes it and the current Wawi value stays untouched.
    const generated: Record<string, string> = {};
    Object.entries(result.payload.generated || {}).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') {
        generated[key] = value;
      }
    });

    setEditableValues((prev) => {
      const next = { ...prev };
      CONTENT_MASTER_CORE_FIELD_KEYS.forEach((field) => {
        if (typeof generated[field] === 'string') {
          next[field] = generated[field] as string;
        }
      });
      return next;
    });

    setWawiExtValues((prev) => {
      const next = { ...prev };
      Object.keys(generated).forEach((key) => {
        if (CORE_FIELD_KEY_SET.has(key)) {
          return;
        }
        if (typeof generated[key] === 'string') {
          next[key] = generated[key] as string;
        }
      });
      return next;
    });

    setDirtyFields((prevDirty) => {
      const merged: DirtyFieldsMap = { ...prevDirty };
      CONTENT_MASTER_CORE_FIELD_KEYS.forEach((field) => {
        if (typeof generated[field] === 'string') {
          merged[field] = true;
        }
      });
      Object.keys(generated).forEach((key) => {
        if (CORE_FIELD_KEY_SET.has(key)) {
          return;
        }
        if (typeof generated[key] === 'string') {
          merged[key] = true;
        }
      });
      return merged;
    });

    // Drop any prior version reference for the just-generated fields so that
    // Save Draft / Apply send `{ text }` (the freshly generated value), not
    // `{ id }` of a stale auto-picked version. Mirrors the single-field
    // `handleGenerateField` / `handleGenerateWawiAttr` / `handleGenerateMerkmal`.
    setUseVersionSelection((prev) => {
      const merged: VersionSelectionMap = { ...prev };
      Object.keys(generated).forEach((key) => {
        if (typeof generated[key] === 'string') {
          merged[key] = false;
        }
      });
      return merged;
    });

    dispatch(
      addNotification({
        type: 'success',
        title: t('success'),
        message: t('content_master_generate_success'),
      })
    );
    setIsGenerateModalOpen(false);
  };

  const handleGenerateField = async (field: ContentMasterCoreFieldKey) => {
    if (!detail) {
      return;
    }
    const result = await dispatch(
      generateContentMasterField({
        wawiArtikelId: productId,
        source,
        language: postLanguage,
        field,
        dataSourceId: dataSourceIdForApi,
      })
    );

    if (!generateContentMasterField.fulfilled.match(result)) {
      return;
    }

    const generatedValue = result.payload.generated?.[field];
    // Empty AI result → skip entirely (same rule as handleGenerateAll): the
    // field stays non-dirty and is excluded from save-draft/apply.
    if (typeof generatedValue === 'string' && generatedValue.trim() !== '') {
      setEditableValues((prev) => ({
        ...prev,
        [field]: generatedValue,
      }));
      setUseVersionSelection((prev) => ({ ...prev, [field]: false }));
      setDirtyFields((prev) => ({ ...prev, [field]: true }));
    }
  };

  const handleGenerateWawiAttr = async (attr: ContentMasterWawiExtendedFieldView) => {
    const key = attr.fieldApiName;
    if (!detail || !key || !canAiGenerateAttributeLike(attr) || wawiFieldsLocked) {
      return;
    }
    const fieldAllowedValues = buildFieldAllowedValuesForGenerate(detail, [key]);

    const result = await dispatch(
      generateContentMasterField({
        wawiArtikelId: productId,
        source,
        language: postLanguage,
        field: key,
        dataSourceId: dataSourceIdForApi,
        fieldAllowedValues,
      })
    );

    if (!generateContentMasterField.fulfilled.match(result)) {
      return;
    }

    const generatedValue = result.payload.generated?.[key];
    // Empty AI result → skip entirely (same rule as handleGenerateAll): the
    // field stays non-dirty and is excluded from save-draft/apply.
    if (typeof generatedValue === 'string' && generatedValue.trim() !== '') {
      setWawiExtValues((prev) => ({ ...prev, [key]: generatedValue }));
      setUseVersionSelection((prev) => ({ ...prev, [key]: false }));
      setDirtyFields((prev) => ({ ...prev, [key]: true }));
    }
  };

  const handleGenerateMerkmal = async (m: ContentMasterWawiExtendedFieldView) => {
    const key = m.fieldApiName;
    if (!detail || !key || !canAiGenerateMerkmal(m) || wawiFieldsLocked) {
      return;
    }
    const fieldAllowedValues = buildFieldAllowedValuesForGenerate(detail, [key]);

    const result = await dispatch(
      generateContentMasterField({
        wawiArtikelId: productId,
        source,
        language: postLanguage,
        field: key,
        dataSourceId: dataSourceIdForApi,
        fieldAllowedValues,
      })
    );

    if (!generateContentMasterField.fulfilled.match(result)) {
      return;
    }

    const generatedValue = result.payload.generated?.[key];
    // Empty AI result → skip entirely (same rule as handleGenerateAll): the
    // field stays non-dirty and is excluded from save-draft/apply.
    if (typeof generatedValue === 'string' && generatedValue.trim() !== '') {
      setWawiExtValues((prev) => ({ ...prev, [key]: generatedValue }));
      setUseVersionSelection((prev) => ({ ...prev, [key]: false }));
      setDirtyFields((prev) => ({ ...prev, [key]: true }));
    }
  };

  /**
   * Apply a `translated` map (returned by `POST /content-master/translate`) to
   * the right-column form state. Shared by Translate-All and per-field
   * Translate so both flows have identical post-success semantics:
   *   - core keys → `editableValues`,
   *   - extension keys → `wawiExtValues`,
   *   - mark each touched key dirty, drop any prior version reference (so
   *     save-draft sends `{ text }`, not `{ id }`).
   * Empty source values come back from the backend as `""`; we write them
   * as-is - that matches what `save-draft` would persist for the target
   * language. Returns the count of keys actually applied.
   */
  const applyTranslationResult = (translated: Record<string, string> | undefined): number => {
    if (!translated) {
      return 0;
    }
    const keys = Object.keys(translated);
    if (!keys.length) {
      return 0;
    }
    setEditableValues((prev) => {
      const next = { ...prev };
      CONTENT_MASTER_CORE_FIELD_KEYS.forEach((field) => {
        if (typeof translated[field] === 'string') {
          next[field] = translated[field];
        }
      });
      return next;
    });
    setWawiExtValues((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        if (CORE_FIELD_KEY_SET.has(key)) {
          return;
        }
        if (typeof translated[key] === 'string') {
          next[key] = translated[key];
        }
      });
      return next;
    });
    setDirtyFields((prev) => {
      const next: DirtyFieldsMap = { ...prev };
      keys.forEach((key) => {
        if (typeof translated[key] === 'string') {
          next[key] = true;
        }
      });
      return next;
    });
    setUseVersionSelection((prev) => {
      const next: VersionSelectionMap = { ...prev };
      keys.forEach((key) => {
        if (typeof translated[key] === 'string') {
          next[key] = false;
        }
      });
      return next;
    });
    return keys.length;
  };

  /**
   * Translate the user-picked subset of fields (driven by the Translate modal
   * checkboxes - same UX pattern as the Generate batch). The backend requires
   * a non-empty `fields[]`; it still silently drops anything it cannot
   * translate (Merkmal entries, numeric / date `wawiAttr_*`), but the picker
   * already prevents those from being checkable, so in normal operation every
   * key we send comes back. We drive the post-success state update from
   * `response.translated` regardless - keys missing from the response leave
   * their inputs untouched.
   */
  const handleTranslateAllFields = async () => {
    if (!detail || !canTranslate) {
      return;
    }
    const fields = selectedTranslateFieldKeys;
    if (fields.length === 0) {
      dispatch(
        addNotification({
          type: 'info',
          title: t('content_master_translate_nothing_to_translate_title'),
          message: t('content_master_translate_select_at_least_one'),
        })
      );
      return;
    }

    const result = await dispatch(
      translateContentMasterFields({
        wawiArtikelId: productId,
        source,
        sourceLanguage: postLanguage,
        targetLanguage,
        dataSourceId: dataSourceIdForApi,
        fields,
        scope: 'all',
      })
    );

    if (!translateContentMasterFields.fulfilled.match(result)) {
      return;
    }

    const applied = applyTranslationResult(result.payload.translated);
    if (applied === 0) {
      dispatch(
        addNotification({
          type: 'info',
          title: t('content_master_translate_nothing_to_translate_title'),
          message: t('content_master_translate_nothing_to_translate'),
        })
      );
      return;
    }

    dispatch(
      addNotification({
        type: 'success',
        title: t('success'),
        message: t('content_master_translate_success', { count: applied }),
      })
    );
    setIsTranslateModalOpen(false);
  };

  /**
   * Per-field Translate, called from the right-column header link in translate
   * mode. Shape mirrors `handleGenerateField` / `handleGenerateWawiAttr` /
   * `handleGenerateMerkmal`. If the backend silently skips the field
   * (Merkmal, numeric `wawiAttr_*`), `translated[key]` will be missing -
   * surface that to the user as a non-blocking info toast and leave the
   * input untouched.
   */
  const handleTranslateField = async (fieldKey: string) => {
    if (!detail || !fieldKey || !canTranslate) {
      return;
    }
    const result = await dispatch(
      translateContentMasterFields({
        wawiArtikelId: productId,
        source,
        sourceLanguage: postLanguage,
        targetLanguage,
        dataSourceId: dataSourceIdForApi,
        fields: [fieldKey],
        scope: 'field',
      })
    );

    if (!translateContentMasterFields.fulfilled.match(result)) {
      return;
    }

    const translated = result.payload.translated || {};
    if (typeof translated[fieldKey] !== 'string') {
      dispatch(
        addNotification({
          type: 'info',
          title: t('content_master_translate_nothing_to_translate_title'),
          message: t('content_master_translate_field_unsupported'),
        })
      );
      return;
    }
    applyTranslationResult({ [fieldKey]: translated[fieldKey] });
  };

  const buildPayloadFields = (): ContentMasterDraftFieldsPayload => {
    const acc: ContentMasterDraftFieldsPayload = {};

    editableFields.forEach((field) => {
      if (useVersionSelection[field] && selectedVersionIds[field] && dirtyFields[field]) {
        acc[field] = { id: selectedVersionIds[field] || null };
        return;
      }
      if (dirtyFields[field]) {
        acc[field] = { text: editableValues[field] };
      }
    });

    extensionKeys.forEach((key) => {
      if (useVersionSelection[key] && selectedVersionIds[key] && dirtyFields[key]) {
        acc[key] = { id: selectedVersionIds[key] || null };
        return;
      }
      if (dirtyFields[key]) {
        acc[key] = { text: wawiExtValues[key] ?? '' };
      }
    });

    return acc;
  };

  const hasUnsavedChanges = useMemo(() => {
    const coreDirty = editableFields.some((field) => !!dirtyFields[field]);
    const extDirty = extensionKeys.some((key) => !!dirtyFields[key]);
    return coreDirty || extDirty;
  }, [dirtyFields, extensionKeys]);

  /**
   * Snapshot of the form taken immediately after the seeding effect (below)
   * applies its `next*` values for the current (productId, source, language)
   * triple. Stored as a serialized JSON string so the divergence check is a
   * single string comparison.
   *
   * Lifecycle:
   *  - cleared (set to `null`) when the user changes product / channel /
   *    language - see the clear-state effect that sits next to the detail
   *    fetch. While `null`, the `beforeunload` effect short-circuits (the
   *    form has no stable baseline to compare against).
   *  - written by the seeding effect on every successful detail load. The
   *    write uses the same `next*` objects passed to `setEditableValues`
   *    etc., so there is no React batching window where the snapshot and
   *    the state could disagree.
   *
   * Compared against the live `editableValues` / `wawiExtValues` /
   * `selectedVersionIds` / `useVersionSelection` - exactly the four pieces
   * of state the user can mutate via input typing, version-pill clicks,
   * AI generation, or AI translation.
   */
  const formSnapshotRef = useRef<string | null>(null);

  /**
   * URL that "owns" the current form snapshot. Captured in the same effect
   * as `formSnapshotRef` so the popstate handler can restore the address
   * bar when intercepting a browser back: by the time popstate fires,
   * `window.location.href` already points at the page the user clicked
   * back INTO, not the one we want to keep them on.
   */
  const snapshotUrlRef = useRef<string>('');

  /**
   * Mirror the four guarded form pieces into refs so the navigation-guard
   * event handlers below can compute divergence synchronously - without
   * having to be reattached on every keystroke. Updating refs in render is
   * intentional: useEffect would lag by one commit and miss state changes
   * that happen between render and the popstate / click event firing.
   */
  const editableValuesRef = useRef(editableValues);
  const wawiExtValuesRef = useRef(wawiExtValues);
  const selectedVersionIdsRef = useRef(selectedVersionIds);
  const useVersionSelectionRef = useRef(useVersionSelection);
  editableValuesRef.current = editableValues;
  wawiExtValuesRef.current = wawiExtValues;
  selectedVersionIdsRef.current = selectedVersionIds;
  useVersionSelectionRef.current = useVersionSelection;

  /**
   * Marketplace listing edits live inside `MarketplaceFieldsSection`, not in the
   * four core-editor state pieces above. The section reports its dirty state as
   * `marketplaceEditorState.canSave`; mirror it into a ref so the navigation
   * guards below also prompt when only marketplace attributes were edited.
   */
  const marketplaceDirtyRef = useRef(false);
  marketplaceDirtyRef.current = marketplaceEditorState.canSave;

  /**
   * Synchronous, ref-only check used by every navigation guard below. Returns
   * `false` when there is no snapshot (form not yet seeded, or the user has
   * already confirmed a leave) or when the live state still matches what
   * was captured at the most recent seeding.
   */
  const isFormDivergedSync = useCallback((): boolean => {
    // A null snapshot means "not seeded yet" or "already consumed on a confirmed
    // leave" - both gate the marketplace-dirty check too, so nulling the snapshot
    // on a confirmed leave silences this guard for both editors.
    if (formSnapshotRef.current == null) {
      return false;
    }
    if (marketplaceDirtyRef.current) {
      return true;
    }
    const current = JSON.stringify({
      e: editableValuesRef.current,
      w: wawiExtValuesRef.current,
      s: selectedVersionIdsRef.current,
      u: useVersionSelectionRef.current,
    });
    return current !== formSnapshotRef.current;
  }, []);

  /**
   * Single entry point for every "user about to leave the product page"
   * code path: the title-bar back button, the global click-capture
   * interceptor below, and the popstate handler. Returns `true` when the
   * caller may proceed with the navigation, `false` when the user cancelled.
   * On confirmed leave, the snapshot is nullified so subsequent guards (the
   * popstate firing right after `navigate(-1)`, the click bubbling further
   * up the DOM, etc.) don't re-prompt.
   */
  const promptLeaveIfDivergedAndConsume = useCallback((): boolean => {
    if (!isFormDivergedSync()) {
      return true;
    }
    const ok = window.confirm(t('content_master_leave_confirm_message'));
    if (ok) {
      formSnapshotRef.current = null;
    }
    return ok;
  }, [isFormDivergedSync, t]);

  /**
   * Native `beforeunload` guard for tab close, hard refresh, or any browser
   * navigation that fully unloads the document. Modern browsers ignore the
   * custom message and show their own generic dialog; `event.preventDefault()`
   * plus a non-empty `returnValue` is still required to trigger it.
   */
  useEffect(() => {
    if (formSnapshotRef.current == null) {
      return;
    }
    const current = JSON.stringify({
      e: editableValues,
      w: wawiExtValues,
      s: selectedVersionIds,
      u: useVersionSelection,
    });
    const coreDiverged = current !== formSnapshotRef.current;
    // Also guard when only the marketplace listing attributes were edited.
    if (!coreDiverged && !marketplaceEditorState.canSave) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    editableValues,
    wawiExtValues,
    selectedVersionIds,
    useVersionSelection,
    marketplaceEditorState.canSave,
  ]);

  /**
   * In-app navigation guard. `beforeunload` does NOT fire on SPA route
   * changes that stay inside the document, so without this the user could
   * silently escape via:
   *   1. The browser back / forward buttons. We do NOT push a history
   *      sentinel on mount - that breaks the existing `navigate(-1)` flow
   *      after Save Draft / Apply (`-1` would land on the duplicate URL
   *      instead of the products list). Instead, when popstate fires we
   *      pushState the snapshot URL synchronously to restore the address
   *      bar, ask the user, and on "yes" run `history.go(-2)` (popping
   *      both the just-pushed entry and the entry the user navigated into).
   *   2. Any in-app `<Link>` (sidebar, header, etc. - these all render as
   *      `<a href>`). We attach a capture-phase document click listener
   *      so we run BEFORE react-router's own click handler that calls
   *      `navigate()`. Modifier-key clicks (Ctrl/Cmd/Shift/Alt) and
   *      `target="_blank"` / `download` anchors are passed through.
   *
   * Both branches null `formSnapshotRef.current` on confirmed leave so the
   * downstream popstate fired by the navigation itself sees no divergence
   * and stays out of the way.
   */
  useEffect(() => {
    const handlePopState = () => {
      if (!isFormDivergedSync()) {
        return;
      }
      // popstate has already changed the URL to the page the user backed
      // INTO. Push the snapshot URL on top so the address bar reads as
      // the product page again while we ask.
      window.history.pushState({ __cmGuardRestore: true }, '', snapshotUrlRef.current);
      const ok = window.confirm(t('content_master_leave_confirm_message'));
      if (ok) {
        formSnapshotRef.current = null;
        // Two steps back: the entry we just pushed, and the entry the
        // user originally backed into via the browser button. Lands on
        // whatever was BEFORE that second entry - typically the products
        // list page, exactly where the user wanted to go.
        window.history.go(-2);
      }
      // On cancel the pushState above already restored the URL - nothing
      // to do.
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      if (event.button !== 0) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const target = event.target as Element | null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) {
        return;
      }
      if (anchor.target && anchor.target !== '_self') {
        return;
      }
      if (anchor.hasAttribute('download')) {
        return;
      }
      if (!isFormDivergedSync()) {
        return;
      }
      const ok = window.confirm(t('content_master_leave_confirm_message'));
      if (ok) {
        formSnapshotRef.current = null;
      } else {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('popstate', handlePopState);
    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [isFormDivergedSync, t]);

  const getVersions = (fieldKey: string): ContentMasterFieldVersionView[] => {
    return getActiveLanguageVersions(detail)[fieldKey] || [];
  };

  const getCurrentVersion = (fieldKey: string): ContentMasterFieldVersionView | undefined =>
    getVersions(fieldKey).find((item) => item.isCurrent);

  const getCoreOriginalValue = (field: ContentMasterCoreFieldKey): string => {
    if (!detail) {
      return '';
    }

    const selectedVersionId = selectedVersionIds[field];
    if (selectedVersionId) {
      const selectedVersion = getVersions(field).find((item) => item.id === selectedVersionId);
      if (selectedVersion && typeof selectedVersion.value === 'string') {
        return selectedVersion.value;
      }
    }

    const value = detail[field];
    return typeof value === 'string' ? value : '';
  };

  /**
   * Display-side label resolution for multi-value Merkmal CSVs. Merkmal
   * versions store comma-separated `kMerkmalWert` ids as their `value`; on
   * the left preview we want human-readable labels joined with ", " instead
   * of bare digits. We do NOT touch the save logic - the underlying CSV is
   * still what gets persisted.
   */
  const resolveMerkmalDisplayLabel = (
    m: ContentMasterWawiExtendedFieldView,
    rawValue: string
  ): string => {
    const ids = parseMerkmalCsvIds(rawValue);
    if (ids.length === 0) {
      // Could be a stale single-label string from before the multi-value
      // migration; preserve whatever the version carried so the UI does not
      // silently swallow it.
      return rawValue.trim();
    }
    return formatMerkmalLabels(m, ids);
  };

  const getExtensionReferenceValue = (key: string): string => {
    if (!detail) {
      return '';
    }
    const ext = (detail.wawiExtendedFields ?? []).find(
      (f) => f.fieldApiName === key
    );
    if (!ext) {
      return '';
    }
    const selectedVersionId = selectedVersionIds[key];
    if (selectedVersionId) {
      const selectedVersion = getVersions(key).find((item) => item.id === selectedVersionId);
      if (selectedVersion && typeof selectedVersion.value === 'string') {
        if (isMerkmalField(ext)) {
          return resolveMerkmalDisplayLabel(ext, selectedVersion.value);
        }
        return selectedVersion.value;
      }
    }
    if (isAttributeLikeField(ext)) {
      return attributeDisplayValue(ext);
    }
    if (isMerkmalField(ext)) {
      // Multi-value display: render every assigned label, ", "-joined. Use
      // the localised array directly so labels match the article-language
      // panel the user sees on the left.
      const currentIds = getMerkmalCurrentIds(ext);
      if (currentIds.length === 0) {
        return '';
      }
      return formatMerkmalLabels(ext, currentIds);
    }
    return '';
  };

  /**
   * `ContentMasterSaveResultView.processingStatus` and
   * `ContentMasterProductView.processingStatus` are two independently generated
   * enums but share the same wire values (`new` / `in_progress` / `done`). We
   * map explicitly to a) get a real type-narrowed value and b) protect against
   * a future drift in either enum.
   */
  const mapSaveResultStatusToProductView = (
    status: ContentMasterSaveResultView.processingStatus | undefined
  ): ContentMasterProductView.processingStatus | null => {
    switch (status) {
      case ContentMasterSaveResultView.processingStatus.NEW:
        return ContentMasterProductView.processingStatus.NEW;
      case ContentMasterSaveResultView.processingStatus.IN_PROGRESS:
        return ContentMasterProductView.processingStatus.IN_PROGRESS;
      case ContentMasterSaveResultView.processingStatus.DONE:
        return ContentMasterProductView.processingStatus.DONE;
      default:
        return null;
    }
  };

  /**
   * Build the URL of the products list with the same channel / language combo
   * the user is currently editing under. Used as the fallback target when the
   * user opened the product via a direct URL (no SPA history): `navigate(-1)`
   * would otherwise leave our app entirely.
   */
  const buildContentMasterListUrl = (): string => {
    const query = new URLSearchParams();
    query.set('source', source);
    const lang = queryLanguage.trim();
    if (lang) {
      query.set('language', lang);
    }
    return `/features/content-master?${query.toString()}`;
  };

  /**
   * Click handler for the back button in the page title. Goes through the
   * same `promptLeaveIfDivergedAndConsume` gate as every other in-app exit
   * path (sidebar links, browser back) - a single confirm covers all of
   * them, and a yes here clears the snapshot so the popstate fired by
   * `navigate(-1)` does not re-prompt.
   */
  const handleBackToContentMasterList = () => {
    if (!promptLeaveIfDivergedAndConsume()) {
      return;
    }
    const listReturn = getContentMasterListReturnUrl();
    if (listReturn) {
      navigate(listReturn);
      return;
    }
    if (cameFromContentMasterListRef.current) {
      navigate(-1);
    } else {
      navigate(buildContentMasterListUrl());
    }
  };

  /**
   * Common post-success flow for Save Draft / Apply to Wawi:
   *   1. patch the just-edited row's status in the cached list (so the user
   *      sees the up-to-date badge without a list refetch),
   *   2. mark the product for the brief highlight on the list page,
   *   3. show the success toast,
   *   4. go back to the list. Two cases:
   *      a) user came from the in-app list (router state flag) → `navigate(-1)`
   *         and tell the list page to skip the on-mount refetch (preserves
   *         scroll + cached list + the just-patched status),
   *      b) no SPA history (direct URL / opened in a new tab) → explicit
   *         navigate to the list URL. We do NOT set `skipNextListFetch` here
   *         because the Redux `list.items` is empty in that case - skipping
   *         the fetch would show an empty list. Highlight + status patch
   *         still apply once the fetch returns and includes our row.
   */
  const finalizeAfterSuccessfulSave = (
    result: { payload: ContentMasterSaveResultView | undefined },
    successMessageKey: 'content_master_saved_draft' | 'content_master_applied'
  ) => {
    const nextStatus = mapSaveResultStatusToProductView(result.payload?.processingStatus);
    if (nextStatus) {
      dispatch(
        applyContentMasterListItemStatus({
          wawiArtikelId: productId,
          processingStatus: nextStatus,
        })
      );
    }

    // Live-patch the list row's `title` after Apply (NOT after Save Draft -
    // drafts are not visible in the list view) when the language we just
    // persisted matches the language the list is currently showing.
    //
    // The new title comes from the backend response (`savedFields.title.value`)
    // - that's the source of truth regardless of whether the user picked a
    // version (`{ id }`) or typed text (`{ text }`) into the form.
    //
    // Language match rules (mirroring the list-page semantics):
    //   - `filters.language` non-empty → simple equality with `languageForPersist`.
    //   - `filters.language` empty     → list shows the shop default language;
    //     match iff `languageForPersist` equals that default.
    if (successMessageKey === 'content_master_applied') {
      const newTitle = result.payload?.savedFields?.title?.value;
      if (typeof newTitle === 'string') {
        const listLanguage = store.getState().contentMaster.filters.language.trim();
        const listEffectiveLanguage =
          listLanguage ||
          pickDefaultWawiLanguageCode(wawiLanguages, workspaceConfig?.defaultLanguage);
        if (listEffectiveLanguage && listEffectiveLanguage === languageForPersist) {
          dispatch(
            applyContentMasterListItemTitle({
              wawiArtikelId: productId,
              title: newTitle,
            })
          );
        }
      }
    }

    dispatch(setPendingContentMasterHighlight(productId));
    dispatch(
      addNotification({
        type: 'success',
        title: t('success'),
        message: t(successMessageKey),
      })
    );

    // The save persisted everything the user changed; we are about to
    // imperatively navigate away. Drop the snapshot so the popstate the
    // upcoming `navigate(-1)` triggers (and any global click capture
    // bubbling from the click that started this flow) cannot re-prompt.
    formSnapshotRef.current = null;

    const listReturn = getContentMasterListReturnUrl();
    if (listReturn) {
      navigate(listReturn);
      return;
    }

    if (cameFromContentMasterListRef.current) {
      dispatch(setSkipNextContentMasterListFetch(true));
      navigate(-1);
    } else {
      navigate(buildContentMasterListUrl());
    }
  };

  /**
   * Picks which language code to send as `language` in `save-draft` / `apply`.
   *
   * In **generate** mode the user has been editing values for the page
   * language (left column reflects that language; right column is an AI
   * generation FOR that language). Behaviour is unchanged from before the
   * translation feature: we send `pageLanguage`.
   *
   * In **translate** mode the values currently sitting in the right column
   * came back from `POST /translate` as a translation INTO `targetLanguage`,
   * so they must be persisted against `targetLanguage`. Sending `pageLanguage`
   * (= source) would write the translated text to the wrong language.
   *
   * No `sourceLanguage` is ever sent to `save-draft` / `apply` - those
   * endpoints only know about a single `language`.
   */
  const languageForPersist: ContentMasterPostLanguage =
    editorMode === 'translate' ? targetLanguage : postLanguage;

  const handleSaveDraft = async () => {
    if (!hasUnsavedChanges) {
      return;
    }

    const result = await dispatch(
      saveContentMasterDraft({
        wawiArtikelId: productId,
        source,
        language: languageForPersist,
        fields: buildPayloadFields(),
        dataSourceId: dataSourceIdForApi,
      })
    );

    if (!saveContentMasterDraft.fulfilled.match(result)) {
      return;
    }

    finalizeAfterSuccessfulSave(result, 'content_master_saved_draft');
  };

  /**
   * Performs the actual apply (pushes the draft to Wawi / the database).
   * Split out from {@link handleApply} so the consent warning can gate it:
   * Apply overwrites live product data and may break production listings.
   */
  const performApply = async () => {
    if (!hasUnsavedChanges) {
      return;
    }

    const result = await dispatch(
      applyContentMasterDraft({
        wawiArtikelId: productId,
        source,
        language: languageForPersist,
        fields: buildPayloadFields(),
        dataSourceId: dataSourceIdForApi,
      })
    );

    if (!applyContentMasterDraft.fulfilled.match(result)) {
      return;
    }

    finalizeAfterSuccessfulSave(result, 'content_master_applied');
  };

  /**
   * Runs the actual apply for whichever editor is on the page: the
   * marketplace (SCX) section applies through its imperative handle, the
   * regular editor through {@link performApply}. Both MUST be reached only
   * via {@link handleApply} so the consent warning gates every apply path.
   */
  const runApplyAction = () => {
    if (isMarketplaceChannel) {
      marketplaceEditorRef.current?.apply();
      return;
    }
    void performApply();
  };

  const handleApply = () => {
    // The marketplace section tracks its own dirty/valid state (`canApply`
    // already disables the button), so the unsaved-changes guard only
    // applies to the regular editor.
    if (!isMarketplaceChannel && !hasUnsavedChanges) {
      return;
    }
    // Skip the warning when the user previously opted out ("don't show again").
    if (isApplyWarningDismissed('single')) {
      runApplyAction();
      return;
    }
    setApplyWarningOpen(true);
  };

  const handleConfirmApplyWarning = () => {
    setApplyWarningOpen(false);
    runApplyAction();
  };

  /**
   * Drop a string into either `editableValues` (core fields) or `wawiExtValues`
   * (Wawi attribute / merkmal extension keys), depending on whether the key is one
   * of the core fields. Used by the version-pill click handlers below to fill the
   * right textarea on selection of a non-current version (and to clear it when
   * the current / Original pill is picked, per the right-column rule:
   *   "filled only when AI just generated, OR a non-current version is selected").
   */
  const writeFieldSeed = (fieldKey: string, value: string) => {
    if ((editableFields as readonly string[]).includes(fieldKey)) {
      setEditableValues((prev) => ({ ...prev, [fieldKey]: value } as FieldValues));
    } else {
      setWawiExtValues((prev) => ({ ...prev, [fieldKey]: value }));
    }
  };

  const renderVersionButtons = (fieldKey: string) => {
    const versions = getVersions(fieldKey);
    // When no version has been promoted to "isCurrent" (e.g. NEW with no versions
    // yet, or IN_PROGRESS where nothing has been applied to Wawi), expose a synthetic
    // "Original" pill so the user can flip the left preview back to the original
    // Wawi value of the field. DONE products always have an isCurrent version, so
    // the button is hidden - the current version pill itself plays that role.
    const showOriginalPill = !versions.some((item) => item.isCurrent);
    return (
      <div className="content-master-version-buttons">
        {showOriginalPill ? (
          <button
            key={ORIGINAL_VERSION_ID}
            type="button"
            className={`content-master-version-button ${
              selectedVersionIds[fieldKey] === ORIGINAL_VERSION_ID
                ? 'content-master-version-button--active'
                : ''
            }`}
            onClick={() => {
              // Original is the "logical current" when there's no isCurrent version.
              // Picking it = "no draft change": reset the right control to its
              // neutral seed (empty for textareas, current saved value for
              // <select>-based Merkmale / fixed-option attributes - see
              // computeNeutralSeed for why), drop the dirty flag. The left preview
              // falls back to the Wawi value because getCoreOriginalValue /
              // getExtensionReferenceValue ignore the sentinel id.
              setSelectedVersionIds((prev) => ({ ...prev, [fieldKey]: ORIGINAL_VERSION_ID }));
              setUseVersionSelection((prev) => ({ ...prev, [fieldKey]: false }));
              setDirtyFields((prev) => ({ ...prev, [fieldKey]: false }));
              writeFieldSeed(fieldKey, detail ? computeNeutralSeed(detail, fieldKey) : '');
            }}
          >
            {t('content_master_version_button_original')}
          </button>
        ) : null}
        {versions.map((version) => {
          const isActive = selectedVersionIds[fieldKey] === version.id;
          return (
            <button
              key={version.id}
              type="button"
              className={`content-master-version-button ${
                isActive ? 'content-master-version-button--active' : ''
              }`}
              onClick={() => {
                const currentVersion = getCurrentVersion(fieldKey);
                const isCurrentSelection = version.id === currentVersion?.id;
                setSelectedVersionIds((prev) => ({ ...prev, [fieldKey]: version.id || '' }));
                setUseVersionSelection((prev) => ({ ...prev, [fieldKey]: !isCurrentSelection }));
                setDirtyFields((prev) => ({ ...prev, [fieldKey]: !isCurrentSelection }));
                // Right-column rule: filled with the version's value when a non-
                // current version is picked; reset to the neutral seed when the
                // current version is re-selected (= "no draft change", same as
                // Original). For <select>-based fields the neutral seed must be
                // the current saved value to avoid HTML auto-defaulting to the
                // first option - see computeNeutralSeed.
                const neutral = detail ? computeNeutralSeed(detail, fieldKey) : '';
                writeFieldSeed(
                  fieldKey,
                  isCurrentSelection
                    ? neutral
                    : (typeof version.value === 'string' ? version.value : neutral)
                );
              }}
            >
              {formatVersionLabel(version)}
              {version.isCurrent ? (
                <span className="content-master-version-button__current">{t('content_master_current')}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  };

  const renderPairedRightSuffix = () => (
    <div className="content-master-field-card__spacer" aria-hidden="true" />
  );

  const renderAttributeEditor = (attr: ContentMasterWawiExtendedFieldView) => {
    const key = attr.fieldApiName;
    if (!key) {
      return null;
    }
    const readOnly = attr.isReadOnly === true || wawiFieldsLocked;
    const value = wawiExtValues[key] ?? '';

    if (readOnly) {
      return (
        <>
          <div className="content-master-field-card__right-main content-master-field-card__right-main--scroll">
            <textarea
              value={value}
              readOnly
              className="content-master-field-card__textarea"
              disabled
            />
          </div>
          {renderPairedRightSuffix()}
        </>
      );
    }

    if (attr.hasFixedOptions && attr.allowedValues?.length) {
      const opts = attr.allowedValues.filter((x) => typeof x === 'string');
      const optionValues = opts.includes(value) ? opts : [...opts, ...(value ? [value] : [])];
      return (
        <>
          <div className="content-master-field-card__right-main">
            <div className="content-master-field-card__control-wrap">
              <select
                className="content-master-field-card__select content-master-field-card__select--paired"
                value={value}
                onChange={(event) => {
                  setWawiExtValues((prev) => ({ ...prev, [key]: event.target.value }));
                  setUseVersionSelection((prev) => ({ ...prev, [key]: false }));
                  setDirtyFromUserEdit(key, event.target.value);
                }}
                disabled={blockActionsWhileDetailLoading || wawiFieldsLocked}
              >
                {optionValues.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {renderPairedRightSuffix()}
        </>
      );
    }

    if (attr.nDatenTyp === 0) {
      return (
        <>
          <div className="content-master-field-card__right-main">
            <div className="content-master-field-card__control-wrap">
              <input
                type="number"
                step={1}
                className="content-master-field-card__input-paired"
                value={value}
                onChange={(event) => {
                  setWawiExtValues((prev) => ({ ...prev, [key]: event.target.value }));
                  setUseVersionSelection((prev) => ({ ...prev, [key]: false }));
                  setDirtyFromUserEdit(key, event.target.value);
                }}
                disabled={blockActionsWhileDetailLoading || wawiFieldsLocked}
              />
            </div>
          </div>
          {renderPairedRightSuffix()}
        </>
      );
    }

    if (attr.nDatenTyp === 1) {
      return (
        <>
          <div className="content-master-field-card__right-main">
            <div className="content-master-field-card__control-wrap">
              <input
                type="text"
                inputMode="decimal"
                className="content-master-field-card__input-paired"
                value={value}
                onChange={(event) => {
                  setWawiExtValues((prev) => ({ ...prev, [key]: event.target.value }));
                  setUseVersionSelection((prev) => ({ ...prev, [key]: false }));
                  setDirtyFromUserEdit(key, event.target.value);
                }}
                disabled={blockActionsWhileDetailLoading || wawiFieldsLocked}
              />
            </div>
          </div>
          {renderPairedRightSuffix()}
        </>
      );
    }

    if (attr.nDatenTyp === 3) {
      return (
        <>
          <div className="content-master-field-card__right-main">
            <div className="content-master-field-card__control-wrap">
              <input
                type="text"
                className="content-master-field-card__input-paired"
                value={value}
                onChange={(event) => {
                  setWawiExtValues((prev) => ({ ...prev, [key]: event.target.value }));
                  setUseVersionSelection((prev) => ({ ...prev, [key]: false }));
                  setDirtyFromUserEdit(key, event.target.value);
                }}
                disabled={blockActionsWhileDetailLoading || wawiFieldsLocked}
              />
            </div>
          </div>
          {renderPairedRightSuffix()}
        </>
      );
    }

    return (
      <>
        <div className="content-master-field-card__right-main content-master-field-card__right-main--scroll">
          <textarea
            value={value}
            onChange={(event) => {
              setWawiExtValues((prev) => ({ ...prev, [key]: event.target.value }));
              setUseVersionSelection((prev) => ({ ...prev, [key]: false }));
              setDirtyFromUserEdit(key, event.target.value);
            }}
            className="content-master-field-card__textarea"
            disabled={blockActionsWhileDetailLoading}
            maxLength={attr.maxLength ?? undefined}
          />
        </div>
        {renderPairedRightSuffix()}
      </>
    );
  };

  /**
   * Multi-value Merkmal editor - inline scrollable checkbox list, one row
   * per allowed `kMerkmalWert`. The underlying form state stays a plain
   * string (canonical CSV of selected ids, sorted ASC, no spaces) so the
   * save / apply payload, version pill clicks (which write the version's
   * `value` straight into `wawiExtValues[key]`), and dirty-tracking all
   * keep working with the existing pipeline.
   *
   * UX rules from the multi-value contract:
   *   - One control per Merkmal entry (the array in `merkmalWertOptions`).
   *   - Pre-selected values come from the canonical CSV currently sitting
   *     in `wawiExtValues[key]` - which is seeded by `computeNeutralSeed`
   *     from `draftValue` / `kMerkmalWerte` on detail load.
   *   - Empty selection serialises as "" and means "remove all values".
   *   - On submit the backend dedups / sorts ASC, so we do too on every
   *     toggle to keep the dirty diff stable.
   */
  const renderMerkmalEditor = (m: ContentMasterWawiExtendedFieldView) => {
    const key = m.fieldApiName;
    if (!key) {
      return null;
    }
    const csv = wawiExtValues[key] ?? '';
    const rawOpts = m.merkmalWertOptions ?? [];
    const opts = rawOpts.filter(
      (o) => o.kMerkmalWert != null
    ) as Array<{ kMerkmalWert: number; label?: string }>;

    const handleMerkmalChange = (nextCsv: string) => {
      setWawiExtValues((prev) => ({ ...prev, [key]: nextCsv }));
      setUseVersionSelection((prev) => ({ ...prev, [key]: false }));
      setDirtyFromUserEdit(key, nextCsv);
    };

    return (
      <>
        <div className="content-master-field-card__right-main">
          <ContentMasterMerkmalMultiEditor
            options={opts}
            value={csv}
            onChange={handleMerkmalChange}
            disabled={blockActionsWhileDetailLoading || wawiFieldsLocked}
          />
        </div>
        {renderPairedRightSuffix()}
      </>
    );
  };

  const setBatchGenerateField = (fieldKey: string, checked: boolean) => {
    setGenerateBatchSelection((prev) => ({ ...prev, [fieldKey]: checked }));
  };

  const selectAllBatchFields = (checked: boolean) => {
    setGenerateBatchSelection((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((k) => {
        // "Select all" restores the default selection - it never ticks the
        // backend-excluded opt-in fields (e.g. urlPath).
        next[k] = checked && !generateDefaultExcluded.has(k);
      });
      return next;
    });
  };

  // Three independent slices of the unified `wawiExtendedFields` array. Order
  // is preserved from the API; sections with zero items must NOT render
  // (per backend spec). Eigene Felder (`type === 'eigene'`) share the same
  // editor and AI-eligibility rules as regular `attribut` items, so we just
  // filter them out of the attribute list rather than introducing a parallel
  // rendering path.
  const articleAttributes = useMemo(
    () => selectArticleAttributes(detail?.wawiExtendedFields),
    [detail?.wawiExtendedFields]
  );
  const eigeneFelder = useMemo(
    () => selectEigeneFelder(detail?.wawiExtendedFields),
    [detail?.wawiExtendedFields]
  );
  const merkmale = useMemo(
    () => selectMerkmale(detail?.wawiExtendedFields),
    [detail?.wawiExtendedFields]
  );

  const aiEligibleAttrs = useMemo(
    () =>
      articleAttributes.filter(
        (a) => !!a.fieldApiName && canAiGenerateAttributeLike(a)
      ),
    [articleAttributes]
  );
  const aiEligibleEigene = useMemo(
    () =>
      eigeneFelder.filter(
        (a) => !!a.fieldApiName && canAiGenerateAttributeLike(a)
      ),
    [eigeneFelder]
  );
  const aiEligibleMerkmale = useMemo(
    () =>
      merkmale.filter((m) => !!m.fieldApiName && canAiGenerateMerkmal(m)),
    [merkmale]
  );

  const showInProgressQueueColumn = useMemo(
    () =>
      !!detail &&
      typeof detail.wawiArtikelId === 'number' &&
      detail.wawiArtikelId === productId &&
      detail.processingStatus === ContentMasterProductView.processingStatus.IN_PROGRESS,
    [detail, productId]
  );

  return (
    <div className="gpsr-content content-master-product-edit">
      {detailLoading ? (
        <div
          className="content-master-product-edit__overlay"
          aria-busy="true"
          aria-live="polite"
        >
          <div className="order-sidebar-spinner" aria-label={t('loading_text')} />
        </div>
      ) : null}

      <div className="gpsr-content-header-wrapper">
        <div className="gpsr-content-header-title">
          <button type="button" onClick={handleBackToContentMasterList}>
            <ArrowLeftIcon />
          </button>
          {t('content_master_back_to_list')}
        </div>
      </div>

      <div className="content-master-detail-panel">
        {/*
          Product summary: table layout (header row = labels, body = values).
          `beforeunload` does not run on in-app navigation; `<Link>` clicks are
          still guarded by the capture-phase document listener (see effect below).
        */}
        <div className="content-master-product-summary-wrap">
          <table className="content-master-product-summary-table">
            <thead>
              <tr>
                {showInProgressQueueColumn ? (
                  <th className="content-master-product-summary-table__th content-master-product-summary-table__th--queue">
                    {t('content_master_in_progress_queue_label')}
                  </th>
                ) : null}
                <th className="content-master-product-summary-table__th">
                  {t('content_master_summary_product')}
                </th>
                <th className="content-master-product-summary-table__th">
                  {t('content_master_summary_sku')}
                </th>
                <th className="content-master-product-summary-table__th">
                  {t('content_master_summary_gtin')}
                </th>
                <th className="content-master-product-summary-table__th">
                  {t('content_master_status')}
                </th>
                <th className="content-master-product-summary-table__th">
                  {t('content_master_summary_last_modified')}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {showInProgressQueueColumn ? (
                  <td className="content-master-product-summary-table__td content-master-product-summary-table__td--queue">
                    {inProgressQueueLoading && !inProgressQueue ? (
                      <div
                        className="content-master-in-progress-queue content-master-in-progress-queue--loading"
                        aria-busy="true"
                        aria-live="polite"
                      >
                        <div className="order-sidebar-spinner" aria-label={t('loading_text')} />
                      </div>
                    ) : inProgressQueue !== null && inProgressQueue.total > 0 ? (
                      <div className="content-master-in-progress-queue">
                        {inProgressQueue.position <= 1 ? (
                          <span
                            className="content-master-in-progress-queue__chevron content-master-in-progress-queue__chevron--disabled"
                            aria-hidden
                          >
                            <svg
                              width="10"
                              height="16"
                              viewBox="0 0 10 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden
                            >
                              <path
                                d="M7.25 15.5C7.051 15.501 6.86 15.4217 6.72 15.28L0.72 9.28C0.427546 8.98721 0.427546 8.51279 0.72 8.22L6.72 2.22C7.01541 1.94467 7.47591 1.95279 7.76152 2.23841C8.04713 2.52402 8.05526 2.98452 7.78 3.28L2.233 8.75L7.78 14.22C8.07246 14.5128 8.07246 14.9872 7.78 15.28C7.64013 15.4217 7.44908 15.501 7.25 15.5Z"
                                fill="currentColor"
                              />
                            </svg>
                          </span>
                        ) : inProgressAdjacentNav.prevTo ? (
                          <Link
                            to={inProgressAdjacentNav.prevTo}
                            state={inProgressQueueLinkState}
                            className="content-master-in-progress-queue__chevron"
                            aria-label={t('content_master_in_progress_queue_prev')}
                          >
                            <svg
                              width="10"
                              height="16"
                              viewBox="0 0 10 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden
                            >
                              <path
                                d="M7.25 15.5C7.051 15.501 6.86 15.4217 6.72 15.28L0.72 9.28C0.427546 8.98721 0.427546 8.51279 0.72 8.22L6.72 2.22C7.01541 1.94467 7.47591 1.95279 7.76152 2.23841C8.04713 2.52402 8.05526 2.98452 7.78 3.28L2.233 8.75L7.78 14.22C8.07246 14.5128 8.07246 14.9872 7.78 15.28C7.64013 15.4217 7.44908 15.501 7.25 15.5Z"
                                fill="currentColor"
                              />
                            </svg>
                          </Link>
                        ) : (
                          <span
                            className="content-master-in-progress-queue__chevron content-master-in-progress-queue__chevron--disabled"
                            aria-hidden
                          >
                            <svg
                              width="10"
                              height="16"
                              viewBox="0 0 10 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden
                            >
                              <path
                                d="M7.25 15.5C7.051 15.501 6.86 15.4217 6.72 15.28L0.72 9.28C0.427546 8.98721 0.427546 8.51279 0.72 8.22L6.72 2.22C7.01541 1.94467 7.47591 1.95279 7.76152 2.23841C8.04713 2.52402 8.05526 2.98452 7.78 3.28L2.233 8.75L7.78 14.22C8.07246 14.5128 8.07246 14.9872 7.78 15.28C7.64013 15.4217 7.44908 15.501 7.25 15.5Z"
                                fill="currentColor"
                              />
                            </svg>
                          </span>
                        )}
                        <span className="content-master-in-progress-queue__count">
                          {t('content_master_in_progress_queue_position', {
                            current: inProgressQueue.position,
                            total: inProgressQueue.total,
                          })}
                        </span>
                        {inProgressQueue.position >= inProgressQueue.total ? (
                          <span
                            className="content-master-in-progress-queue__chevron content-master-in-progress-queue__chevron--disabled"
                            aria-hidden
                          >
                            <svg
                              width="10"
                              height="16"
                              viewBox="0 0 10 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden
                            >
                              <path
                                d="M2.75 15.5C2.55093 15.501 2.35987 15.4217 2.22 15.28C1.92755 14.9872 1.92755 14.5128 2.22 14.22L7.767 8.75L2.22 3.28C1.94467 2.98452 1.95279 2.52402 2.23841 2.23841C2.52402 1.95279 2.98452 1.94467 3.28 2.22L9.28 8.22C9.57245 8.51279 9.57245 8.98721 9.28 9.28L3.28 15.28C3.14013 15.4217 2.94908 15.501 2.75 15.5Z"
                                fill="currentColor"
                              />
                            </svg>
                          </span>
                        ) : inProgressAdjacentNav.nextTo ? (
                          <Link
                            to={inProgressAdjacentNav.nextTo}
                            state={inProgressQueueLinkState}
                            className="content-master-in-progress-queue__chevron"
                            aria-label={t('content_master_in_progress_queue_next')}
                          >
                            <svg
                              width="10"
                              height="16"
                              viewBox="0 0 10 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden
                            >
                              <path
                                d="M2.75 15.5C2.55093 15.501 2.35987 15.4217 2.22 15.28C1.92755 14.9872 1.92755 14.5128 2.22 14.22L7.767 8.75L2.22 3.28C1.94467 2.98452 1.95279 2.52402 2.23841 2.23841C2.52402 1.95279 2.98452 1.94467 3.28 2.22L9.28 8.22C9.57245 8.51279 9.57245 8.98721 9.28 9.28L3.28 15.28C3.14013 15.4217 2.94908 15.501 2.75 15.5Z"
                                fill="currentColor"
                              />
                            </svg>
                          </Link>
                        ) : (
                          <span
                            className="content-master-in-progress-queue__chevron content-master-in-progress-queue__chevron--disabled"
                            aria-hidden
                          >
                            <svg
                              width="10"
                              height="16"
                              viewBox="0 0 10 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden
                            >
                              <path
                                d="M2.75 15.5C2.55093 15.501 2.35987 15.4217 2.22 15.28C1.92755 14.9872 1.92755 14.5128 2.22 14.22L7.767 8.75L2.22 3.28C1.94467 2.98452 1.95279 2.52402 2.23841 2.23841C2.52402 1.95279 2.98452 1.94467 3.28 2.22L9.28 8.22C9.57245 8.51279 9.57245 8.98721 9.28 9.28L3.28 15.28C3.14013 15.4217 2.94908 15.501 2.75 15.5Z"
                                fill="currentColor"
                              />
                            </svg>
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="content-master-product-summary-table__value">-</span>
                    )}
                  </td>
                ) : null}
                <td className="content-master-product-summary-table__td">
                  <div className="content-master-product-summary__product-row">
                    <ContentMasterProductThumb imageUrl={detail?.mainImageUrl} alt="" />
                    <div className="content-master-product-summary__title">{detail?.title || '-'}</div>
                  </div>
                </td>
                <td className="content-master-product-summary-table__td">
                  <span className="content-master-product-summary-table__value">{detail?.sku || '-'}</span>
                </td>
                <td className="content-master-product-summary-table__td">
                  <span className="content-master-product-summary-table__value">{detail?.gtin || '-'}</span>
                </td>
                <td className="content-master-product-summary-table__td">
                  {detail ? (
                    <span className="content-master-status-cell">
                      <span
                        className={getContentMasterStatusClassName(
                          detail.processingStatus ?? ContentMasterProductView.processingStatus.NEW
                        )}
                      >
                        {t(
                          contentMasterStatusLabelKey(
                            detail.processingStatus ?? ContentMasterProductView.processingStatus.NEW
                          )
                        )}
                      </span>
                      <ContentMasterOtherSourcesBadge
                        items={detail.otherSourceProcessing}
                        resolveSourceLabel={resolveSourceLabel}
                      />
                    </span>
                  ) : (
                    <span className="content-master-product-summary-table__value">-</span>
                  )}
                </td>
                <td className="content-master-product-summary-table__td">
                  <span className="content-master-product-summary-table__value">
                    {formatDetailDate(getMaxDetailModifiedMs(detail))}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="content-master-editor content-master-editor--paired">
          <div className="content-master-editor__head-left">
            <div className="content-master-column-header">
              <h2 className="content-master-column__title">
                {pageLanguageDisplay
                  ? t('content_master_original_with_language', { language: pageLanguageDisplay })
                  : t('content_master_original')}
                <span className="content-master-readonly-badge content-master-readonly-badge--inline">
                  {t('content_master_readonly')}
                </span>
              </h2>
            </div>
            {/*
              Toolbar under the left column title. Channel always renders here.
              The page-language select renders here ONLY in generate mode - in
              translate mode the same `pageLanguage` state is exposed via
              "Source Language" in the RIGHT column header instead, so we don't
              duplicate it on the left.
            */}
            <div className="content-master-detail-filters content-master-detail-filters--inline">
              {showChannelSelect && (
                <div
                  className="content-master-detail-filter"
                  title={(() => {
                    const sel = channelOptionsFlat.find((o) => o.value === source);
                    if (!sel) {
                      return undefined;
                    }
                    if (sel.isMaster) {
                      return t('content_master_channel_master_tooltip');
                    }
                    return formatContentMasterChannelMeta(sel);
                  })()}
                >
                  <span className="content-master-detail-filter__label">
                    {t('content_master_detail_channel_prefix')}
                  </span>
                  <select
                    className="content-master-detail-filter__select"
                    value={source}
                    onChange={(e) => {
                      const next = e.target.value as ContentMasterSource;
                      setChannelInUrl(next);
                      writeLastSelectedChannelId(
                        workspaceConfig?.dataSourceId ?? null,
                        next
                      );
                    }}
                    aria-label={t('content_master_all_channels')}
                  >
                    {channelGroups.map((group) => {
                      const groupLabel =
                        group.kind === ContentMasterWorkspacePlatformView.kind.PLATFORM
                          ? t('content_master_channel_group_platforms')
                          : group.label ?? t('content_master_channel_group_shops');
                      return (
                        <optgroup key={group.key} label={groupLabel}>
                          {group.options.map((opt) => (
                            <option
                              key={opt.value}
                              value={opt.value}
                              title={
                                opt.isMaster
                                  ? t('content_master_channel_master_tooltip')
                                  : opt.hasContent
                                  ? formatContentMasterChannelMeta(opt)
                                  : t('content_master_channel_no_content_tooltip')
                              }
                            >
                              {opt.isMaster ? '★ ' : ''}
                              {opt.name}
                              {!opt.active
                                ? ` ${t('content_master_channel_inactive_suffix')}`
                                : ''}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
              )}
              {editorMode === 'generate' ? (
                <div className="content-master-detail-filter">
                  <span className="content-master-detail-filter__label">
                    {t('content_master_detail_language_prefix')}
                    {hasOtherLanguageDraft ? (
                      <span
                        className="content-master-dirty-marker"
                        title={t('content_master_language_state_draft')}
                        aria-label={t('content_master_language_state_draft')}
                      >
                        {' '}●
                      </span>
                    ) : null}
                  </span>
                  <select
                    className="content-master-detail-filter__select"
                    value={languageSelectValue}
                    disabled={wawiLanguagesLoading && wawiLanguages.length === 0}
                    onChange={(e) => setLanguageInUrl(e.target.value)}
                    aria-label={t('language')}
                  >
                    <option value="">{t('content_master_language_shop_default')}</option>
                    {wawiLanguages.map((lang, idx) => {
                      const code = wawiLanguageToApiValue(lang);
                      if (!code) {
                        return null;
                      }
                      const decorated = formatLanguageOptionLabel(
                        code,
                        wawiLanguageLabel(lang, preferDeUi)
                      );
                      return (
                        <option
                          key={`gen-${code}-${lang.kSprache ?? idx}`}
                          value={code}
                          title={decorated.title}
                        >
                          {decorated.text}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ) : null}
            </div>
          </div>
          <div className="content-master-editor__head-right">
            <div className="content-master-column-header">
              {/* <h2 className="content-master-column__title">
                {editorMode === 'translate'
                  ? t('content_master_translation')
                  : t('content_master_ai_generated')}
              </h2> */}
              {/* Marketplace product: the editor-mode toggle + "generate all"
                  are replaced by a single "Generate" button that opens the
                  shared confirm modal (required/recommended/optional) and posts
                  to the synchronous `POST /marketplace-listing/generate-sync`.
                  Normal products keep the Generate/Translate toggle as before. */}
              {isMarketplaceChannel ? (
                <div className="content-master-generate-all-wrap">
                  <button
                    type="button"
                    className="btn btn-primary content-master-generate-all-btn"
                    onClick={() => setIsMarketplaceGenerateModalOpen(true)}
                    disabled={
                      blockActionsWhileDetailLoading ||
                      !canCmWrite ||
                      marketplaceGenerating ||
                      generateSyncSubmitting ||
                      marketplaceEditorState.saving ||
                      marketplaceEditorState.applying ||
                      // Attribute schema not imported yet - generation would only
                      // fail; the stepper in the attributes section (step 2:
                      // request the sync) tells the user what to do.
                      detail?.marketplace?.status ===
                        MarketplaceProductFieldsView.status.CATEGORY_ATTRIBUTES_MISSING
                    }
                    title={
                      !canCmWrite
                        ? t('subuser_no_permission_tooltip')
                        : detail?.marketplace?.status ===
                          MarketplaceProductFieldsView.status.CATEGORY_ATTRIBUTES_MISSING
                        ? t('marketplace_listing_generate_blocked_attrs_missing')
                        : undefined
                    }
                  >
                    <ContentMasterSparkleIcon />
                    {marketplaceGenerating || generateSyncSubmitting
                      ? t('loading_text')
                      : t('marketplace_listing_generate')}
                  </button>
                </div>
              ) : (
                <>
                  <div
                    className="content-master-mode-toggle"
                    role="group"
                    aria-label={t('content_master_editor_mode')}
                  >
                    <button
                      type="button"
                      className={`content-master-mode-toggle__btn${
                        editorMode === 'generate' ? ' content-master-mode-toggle__btn--active' : ''
                      }`}
                      onClick={() => setEditorMode('generate')}
                      aria-pressed={editorMode === 'generate'}
                      disabled={blockActionsWhileDetailLoading}
                    >
                      {t('content_master_generate_content')}
                    </button>
                    <button
                      type="button"
                      className={`content-master-mode-toggle__btn${
                        editorMode === 'translate' ? ' content-master-mode-toggle__btn--active' : ''
                      }`}
                      onClick={() => setEditorMode('translate')}
                      aria-pressed={editorMode === 'translate'}
                      disabled={blockActionsWhileDetailLoading || wawiLanguages.length < 2}
                      title={
                        wawiLanguages.length < 2
                          ? t('content_master_translate_requires_two_languages')
                          : undefined
                      }
                    >
                      {t('content_master_translate_mode')}
                    </button>
                  </div>
                  <div className="content-master-generate-all-wrap">
                    {editorMode === 'translate' ? (
                      <button
                        type="button"
                        className="btn btn-primary content-master-generate-all-btn"
                        onClick={openTranslateModal}
                        disabled={blockActionsWhileDetailLoading || !detail || !canTranslate || !canCmWrite}
                        title={
                          !canCmWrite
                            ? t('subuser_no_permission_tooltip')
                            : translateDisabledReason === 'same_language'
                            ? t('content_master_translate_same_language_hint')
                            : undefined
                        }
                      >
                        <ContentMasterSparkleIcon />
                        {translateAllLoading
                          ? t('loading_text')
                          : t('content_master_translate_all_fields')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary content-master-generate-all-btn"
                        onClick={openGenerateModal}
                        disabled={blockActionsWhileDetailLoading || !detail || !canCmWrite}
                        title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
                      >
                        <ContentMasterSparkleIcon />
                        {generateAllLoading
                          ? t('loading_text')
                          : t('content_master_generate_all_fields')}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            {/*
              Right column toolbar. In generate mode the right column has no
              filters of its own - the page language lives in the left column
              header (Anton's spec). In translate mode we render the Source +
              Target selects here. Source mirrors the same `pageLanguage`
              state as the left column would in generate mode.
            */}
            {editorMode === 'translate' ? (
              <div className="content-master-detail-filters content-master-detail-filters--inline">
                <div className="content-master-detail-filter">
                  <span className="content-master-detail-filter__label">
                    {t('content_master_translate_source_language')}
                    {hasOtherLanguageDraft ? (
                      <span
                        className="content-master-dirty-marker"
                        title={t('content_master_language_state_draft')}
                        aria-label={t('content_master_language_state_draft')}
                      >
                        {' '}●
                      </span>
                    ) : null}
                  </span>
                  <select
                    className="content-master-detail-filter__select"
                    value={languageSelectValue}
                    disabled={wawiLanguagesLoading && wawiLanguages.length === 0}
                    onChange={(e) => setLanguageInUrl(e.target.value)}
                    aria-label={t('content_master_translate_source_language')}
                  >
                    <option value="">{t('content_master_language_shop_default')}</option>
                    {wawiLanguages.map((lang, idx) => {
                      const code = wawiLanguageToApiValue(lang);
                      if (!code) {
                        return null;
                      }
                      const decorated = formatLanguageOptionLabel(
                        code,
                        wawiLanguageLabel(lang, preferDeUi)
                      );
                      return (
                        <option
                          key={`src-${code}-${lang.kSprache ?? idx}`}
                          value={code}
                          title={decorated.title}
                        >
                          {decorated.text}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="content-master-detail-filter">
                  <span className="content-master-detail-filter__label">
                    {t('content_master_translate_target_language')}
                    {hasOtherLanguageDraft ? (
                      <span
                        className="content-master-dirty-marker"
                        title={t('content_master_language_state_draft')}
                        aria-label={t('content_master_language_state_draft')}
                      >
                        {' '}●
                      </span>
                    ) : null}
                  </span>
                  <select
                    className="content-master-detail-filter__select"
                    value={targetLanguage}
                    disabled={wawiLanguagesLoading && wawiLanguages.length === 0}
                    onChange={(e) => setTargetLanguageInUrl(e.target.value)}
                    aria-label={t('content_master_translate_target_language')}
                  >
                    {/*
                      Bound to the RESOLVED `targetLanguage` so the user always
                      sees which language Translate will write to. A "Shop
                      default" placeholder would be misleading - the sensible
                      default for target is "first non-source language", not
                      the shop default. The fallback `<option>` covers the
                      transient case where workspace languages haven't loaded
                      yet but `targetLanguage` already resolved to something.
                    */}
                    {!wawiLanguages.some((l) => wawiLanguageToApiValue(l) === targetLanguage) ? (
                      <option value={targetLanguage}>{targetLanguage || '-'}</option>
                    ) : null}
                    {wawiLanguages.map((lang, idx) => {
                      const code = wawiLanguageToApiValue(lang);
                      if (!code) {
                        return null;
                      }
                      const decorated = formatLanguageOptionLabel(
                        code,
                        wawiLanguageLabel(lang, preferDeUi)
                      );
                      return (
                        <option
                          key={`tgt-${code}-${lang.kSprache ?? idx}`}
                          value={code}
                          title={decorated.title}
                        >
                          {decorated.text}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            ) : null}
          </div>

          {/* On a marketplace channel the core text fields live in the
              dedicated MarketplaceFieldsSection (single editor); hide the
              generic core cards here to avoid a duplicate, unusable editor. */}
          {(isMarketplaceChannel ? [] : editableFields).map((field) => {
            const isHtmlField = HTML_PREVIEW_CORE_FIELDS.has(field);
            const originalValue = getCoreOriginalValue(field);
            const editableValue = editableValues[field];
            return (
            <React.Fragment key={`pair-core-${field}`}>
              <article className="content-master-field-card content-master-field-card--paired content-master-field-card--left">
                <header className="content-master-field-card__header">
                  <h3>
                    {t(FIELD_LABELS[field])}
                    {dirtyFields[field] ? (
                      <span className="content-master-dirty-marker" title={t('saveText')}>
                        ●
                      </span>
                    ) : null}
                    {isHtmlField ? (
                      <button
                        type="button"
                        className="content-master-preview-button"
                        onClick={() => openHtmlPreview(t(FIELD_LABELS[field]), originalValue)}
                        disabled={!originalValue}
                        aria-label={t('content_master_preview_html')}
                        title={t('content_master_preview_html')}
                      >
                        <ContentMasterEyeIcon />
                      </button>
                    ) : null}
                  </h3>
                </header>
                <div className="content-master-field-card__scroll-body">
                  {originalValue || '-'}
                </div>
                {renderVersionButtons(field)}
              </article>
              <article className="content-master-field-card content-master-field-card--paired content-master-field-card--right">
                <header className="content-master-field-card__header">
                  <h3>{t(FIELD_LABELS[field])}
                    
                    {isHtmlField ? (
                      <button
                        type="button"
                        className="content-master-preview-button"
                        onClick={() => openHtmlPreview(t(FIELD_LABELS[field]), editableValue)}
                        disabled={!editableValue}
                        aria-label={t('content_master_preview_html')}
                        title={t('content_master_preview_html')}
                      >
                        <ContentMasterEyeIcon />
                      </button>
                    ) : null}
                    <FieldLengthCounter
                      value={editableValue}
                      maxLength={detail?.fieldMaxLengths?.[field]}
                    />

                  </h3>
                  {editorMode === 'translate' ? (
                    <button
                      type="button"
                      className="content-master-link-button"
                      onClick={() => handleTranslateField(field)}
                      disabled={
                        blockActionsWhileDetailLoading ||
                        !!translateFieldLoading[field] ||
                        !canTranslate ||
                        !canCmWrite
                      }
                      title={
                        !canCmWrite
                          ? t('subuser_no_permission_tooltip')
                          : translateDisabledReason === 'same_language'
                          ? t('content_master_translate_same_language_hint')
                          : undefined
                      }
                    >
                      {translateFieldLoading[field]
                        ? t('loading_text')
                        : t('content_master_translate')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="content-master-link-button"
                      onClick={() => handleGenerateField(field)}
                      disabled={blockActionsWhileDetailLoading || !!generateFieldLoading[field] || !canCmWrite}
                      title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
                    >
                      {generateFieldLoading[field] ? t('loading_text') : t('content_master_generate_field')}
                    </button>
                  )}
                </header>
                <div className="content-master-field-card__right-main content-master-field-card__right-main--scroll">
                  <textarea
                    value={editableValues[field]}
                    onChange={(event) => {
                      setEditableValues((prev) => ({ ...prev, [field]: event.target.value }));
                      setUseVersionSelection((prev) => ({ ...prev, [field]: false }));
                      setDirtyFromUserEdit(field, event.target.value);
                    }}
                    className="content-master-field-card__textarea"
                    disabled={blockActionsWhileDetailLoading}
                    maxLength={detail?.fieldMaxLengths?.[field] ?? undefined}
                  />
                </div>
                <div className="content-master-field-card__spacer" aria-hidden="true" />
              </article>
            </React.Fragment>
            );
          })}

          {/*
            Three Wawi extension sections, in the order required by the
            backend spec: article attributes → Eigene Felder → Merkmale.
            We hide a section entirely when its slice is empty. Both
            `attribut` and `eigene` items share the exact same editor/AI
            rules, so we render them through a single inline helper to avoid
            duplicating ~70 lines of JSX.
          */}
          {(() => {
            const renderAttributeLikePair = (
              attr: ContentMasterWawiExtendedFieldView,
              keyPrefix: string
            ) => {
              const key = attr.fieldApiName;
              if (!key) {
                return null;
              }
              const showGen = canAiGenerateAttributeLike(attr) && !wawiFieldsLocked;
              // Mirrors renderAttributeEditor: editor is a <select> iff not
              // read-only and has fixed options.
              const rightIsSelect =
                attr.isReadOnly !== true &&
                Boolean(
                  attr.hasFixedOptions &&
                    attr.allowedValues &&
                    attr.allowedValues.length > 0
                );
              const attrSelectCls = rightIsSelect
                ? ' content-master-field-card--select'
                : '';
              return (
                <React.Fragment key={`${keyPrefix}-${key}`}>
                  <article
                    className={`content-master-field-card content-master-field-card--paired content-master-field-card--left${attrSelectCls}`}
                  >
                    <header className="content-master-field-card__header">
                      <h3>
                        {attributeLikeLabel(attr, key)}
                        {dirtyFields[key] ? (
                          <span
                            className="content-master-dirty-marker"
                            title={t('saveText')}
                          >
                            ●
                          </span>
                        ) : null}
                        {attr.isReadOnly ? (
                          <span className="content-master-readonly-badge">
                            {t('content_master_readonly')}
                          </span>
                        ) : null}
                      </h3>
                    </header>
                    <div className="content-master-field-card__scroll-body">
                      {getExtensionReferenceValue(key) || '-'}
                    </div>
                    {renderVersionButtons(key)}
                  </article>
                  <article
                    className={`content-master-field-card content-master-field-card--paired content-master-field-card--right${attrSelectCls}`}
                  >
                    <header className="content-master-field-card__header">
                      <h3>
                        {attributeLikeLabel(attr, key)}
                        {wawiFieldsLocked ? (
                          <ContentMasterProBadge className="content-master-pro-badge--inline" />
                        ) : null}
                        <FieldLengthCounter
                          value={wawiExtValues[key] ?? ''}
                          maxLength={attr.maxLength}
                        />
                      </h3>
                      {showGen ? (
                        editorMode === 'translate' ? (
                          <button
                            type="button"
                            className="content-master-link-button"
                            onClick={() => handleTranslateField(key)}
                            disabled={
                              blockActionsWhileDetailLoading ||
                              !!translateFieldLoading[key] ||
                              !canTranslate ||
                              !canCmWrite
                            }
                            title={
                              !canCmWrite
                                ? t('subuser_no_permission_tooltip')
                                : translateDisabledReason === 'same_language'
                                ? t('content_master_translate_same_language_hint')
                                : undefined
                            }
                          >
                            {translateFieldLoading[key]
                              ? t('loading_text')
                              : t('content_master_translate')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="content-master-link-button"
                            onClick={() => handleGenerateWawiAttr(attr)}
                            disabled={
                              blockActionsWhileDetailLoading ||
                              !!generateFieldLoading[key] ||
                              !canCmWrite
                            }
                            title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
                          >
                            {generateFieldLoading[key]
                              ? t('loading_text')
                              : t('content_master_generate_field')}
                          </button>
                        )
                      ) : null}
                    </header>
                    {renderAttributeEditor(attr)}
                  </article>
                </React.Fragment>
              );
            };

            return (
              <>
                {articleAttributes.length > 0 ? (
                  <h2 className="content-master-editor__subtitle-row content-master-column__subtitle">
                    {t('content_master_wawi_article_attributes')}
                    {wawiFieldsLocked ? (
                      <ContentMasterProBadge className="content-master-pro-badge--section" />
                    ) : null}
                  </h2>
                ) : null}
                {articleAttributes.map((attr) =>
                  renderAttributeLikePair(attr, 'pair-attr')
                )}

                {eigeneFelder.length > 0 ? (
                  <h2 className="content-master-editor__subtitle-row content-master-column__subtitle">
                    {t('content_master_wawi_eigene_felder')}
                    {wawiFieldsLocked ? (
                      <ContentMasterProBadge className="content-master-pro-badge--section" />
                    ) : null}
                  </h2>
                ) : null}
                {eigeneFelder.map((attr) =>
                  renderAttributeLikePair(attr, 'pair-eigene')
                )}
              </>
            );
          })()}

          {merkmale.length > 0 ? (
            <h2 className="content-master-editor__subtitle-row content-master-column__subtitle">
              {t('content_master_wawi_characteristics')}
              {wawiFieldsLocked ? (
                <ContentMasterProBadge className="content-master-pro-badge--section" />
              ) : null}
            </h2>
          ) : null}

          {merkmale.map((m) => {
            const key = m.fieldApiName;
            if (!key) {
              return null;
            }
            const showMerkmalGen = canAiGenerateMerkmal(m) && !wawiFieldsLocked;
            const merkmalValidOpts = (m.merkmalWertOptions ?? []).filter(
              (o) => o.kMerkmalWert != null
            );
            const merkmalOptionCount = merkmalValidOpts.length;
            // The right-column editor reads its checked state from the live
            // `wawiExtValues[key]` CSV, so the counter must mirror the same
            // source - picking up version-pill clicks and AI generations
            // without lagging a render behind. Use `parseMerkmalCsvIds` to
            // de-dup / drop invalid fragments, matching what the editor
            // ultimately persists on save.
            const merkmalSelectedCount = parseMerkmalCsvIds(
              wawiExtValues[key] ?? ''
            ).length;
            const merkmalHeading = merkmalLabel(m, key);
            const merkmalCounter =
              merkmalOptionCount > 0 ? (
                <span className="content-master-merkmal-count">
                  {' '}
                  ({t('content_master_merkmal_selected_count', {
                    selected: merkmalSelectedCount,
                    total: merkmalOptionCount,
                  })})
                </span>
              ) : null;
            return (
              <React.Fragment key={`pair-merkmal-${key}`}>
                <article className="content-master-field-card content-master-field-card--paired content-master-field-card--left">
                  <header className="content-master-field-card__header">
                    <h3>
                      {merkmalHeading}
                      {merkmalCounter}
                      {dirtyFields[key] ? (
                        <span className="content-master-dirty-marker" title={t('saveText')}>
                          ●
                        </span>
                      ) : null}
                    </h3>
                  </header>
                  <div className="content-master-field-card__scroll-body">
                    {getExtensionReferenceValue(key) || '-'}
                  </div>
                  {renderVersionButtons(key)}
                </article>
                <article className="content-master-field-card content-master-field-card--paired content-master-field-card--right">
                  <header className="content-master-field-card__header">
                    <h3>
                      {merkmalHeading}
                      {merkmalCounter}
                      {wawiFieldsLocked ? (
                        <ContentMasterProBadge className="content-master-pro-badge--inline" />
                      ) : null}
                    </h3>
                    {showMerkmalGen ? (
                      editorMode === 'translate' ? (
                        <button
                          type="button"
                          className="content-master-link-button"
                          onClick={() => handleTranslateField(key)}
                          disabled={
                            blockActionsWhileDetailLoading ||
                            !!translateFieldLoading[key] ||
                            !canTranslate ||
                            !canCmWrite
                          }
                          title={
                            !canCmWrite
                              ? t('subuser_no_permission_tooltip')
                              : translateDisabledReason === 'same_language'
                              ? t('content_master_translate_same_language_hint')
                              : undefined
                          }
                        >
                          {translateFieldLoading[key]
                            ? t('loading_text')
                            : t('content_master_translate')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="content-master-link-button"
                          onClick={() => handleGenerateMerkmal(m)}
                          disabled={blockActionsWhileDetailLoading || !!generateFieldLoading[key] || !canCmWrite}
                          title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
                        >
                          {generateFieldLoading[key] ? t('loading_text') : t('content_master_generate_field')}
                        </button>
                      )
                    ) : null}
                  </header>
                  {renderMerkmalEditor(m)}
                </article>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {detail?.marketplace ? (
        <MarketplaceFieldsSection
          ref={marketplaceEditorRef}
          marketplace={detail.marketplace}
          source={source}
          wawiArtikelId={productId}
          language={detail.language || postLanguage}
          languageVersions={detail.languageVersions}
          dataSourceId={dataSourceIdForApi}
          processingStatus={detail.processingStatus}
          bulkErrorMessage={detail.bulkErrorMessage}
          onChanged={reloadDetail}
          onStateChange={handleMarketplaceEditorState}
        />
      ) : null}

      {isMarketplaceChannel && detail ? (
        <BulkGenerateConfirmModal
          variant="single"
          isOpen={isMarketplaceGenerateModalOpen}
          onClose={() => setIsMarketplaceGenerateModalOpen(false)}
          selectedCount={1}
          platforms={workspaceConfig?.platforms}
          languages={wawiLanguages}
          defaultWorkspaceLanguage={workspaceConfig?.defaultLanguage}
          initialSource={source}
          initialLanguage={postLanguage}
          preferDeUi={preferDeUi}
          isSubmitting={generateSyncSubmitting}
          dataSourceId={dataSourceIdForApi}
          selectedProducts={[detail]}
          publicConfigs={workspaceConfig?.publicConfigs ?? null}
          onConfirm={handleConfirmMarketplaceGenerateSync}
        />
      ) : null}

      {isGenerateModalOpen ? (
        <div
          className="content-master-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !generateAllLoading) {
              closeGenerateModal();
            }
          }}
        >
          <div
            className="content-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="content-master-generate-modal-title"
          >
            <div className="content-master-modal__header">
              <h3 id="content-master-generate-modal-title" className="content-master-modal__title">
                {t('content_master_generate_pick_fields')}
              </h3>
              <button
                type="button"
                className="content-master-modal__close"
                onClick={closeGenerateModal}
                disabled={generateAllLoading}
                aria-label={t('cancelText')}
              >
                ×
              </button>
            </div>

            <div className="content-master-modal__toolbar">
              <button
                type="button"
                className="content-master-link-button"
                onClick={() => selectAllBatchFields(true)}
                disabled={generateAllLoading}
              >
                {t('content_master_generate_select_all')}
              </button>
              <span className="content-master-modal__toolbar-sep" aria-hidden>
                ·
              </span>
              <button
                type="button"
                className="content-master-link-button"
                onClick={() => selectAllBatchFields(false)}
                disabled={generateAllLoading}
              >
                {t('content_master_generate_deselect_all')}
              </button>
              <span className="content-master-modal__toolbar-count">
                {selectedGenerateFieldKeys.length}
                {' / '}
                {Object.keys(generateBatchSelection).length}
              </span>
            </div>

            <div
              className="content-master-modal__body"
              role="group"
              aria-label={t('content_master_generate_pick_fields')}
            >
              <div className="content-master-generate-field-checkboxes">
                <span className="content-master-generate-field-group-title">
                  {t('content_master_generate_group_core')}
                </span>
                {CONTENT_MASTER_CORE_FIELD_KEYS.map((field) => (
                  <label key={`modal-batch-core-${field}`} className="content-master-generate-field-check">
                    <input
                      type="checkbox"
                      checked={!!generateBatchSelection[field]}
                      onChange={(e) => setBatchGenerateField(field, e.target.checked)}
                      disabled={generateAllLoading}
                    />
                    <span>{t(FIELD_LABELS[field])}</span>
                  </label>
                ))}

                {aiEligibleAttrs.length > 0 ? (
                  <>
                    <span className="content-master-generate-field-group-title">
                      {t('content_master_generate_group_wawi_attrs')}
                      {wawiFieldsLocked ? (
                        <ContentMasterProBadge className="content-master-pro-badge--inline" />
                      ) : null}
                    </span>
                    {aiEligibleAttrs.map((attr) => {
                      const k = attr.fieldApiName!;
                      return (
                        <label
                          key={`modal-batch-attr-${k}`}
                          className={`content-master-generate-field-check${
                            wawiFieldsLocked ? ' content-master-generate-field-check--locked' : ''
                          }`}
                          title={
                            wawiFieldsLocked
                              ? t('cm_entitlements_starter_wawi_disabled')
                              : undefined
                          }
                        >
                          <input
                            type="checkbox"
                            checked={!!generateBatchSelection[k]}
                            onChange={(e) => setBatchGenerateField(k, e.target.checked)}
                            disabled={generateAllLoading || wawiFieldsLocked}
                          />
                          <span>{attributeLikeLabel(attr, k)}</span>
                        </label>
                      );
                    })}
                  </>
                ) : null}

                {aiEligibleEigene.length > 0 ? (
                  <>
                    <span className="content-master-generate-field-group-title">
                      {t('content_master_generate_group_eigene_felder')}
                      {wawiFieldsLocked ? (
                        <ContentMasterProBadge className="content-master-pro-badge--inline" />
                      ) : null}
                    </span>
                    {aiEligibleEigene.map((attr) => {
                      const k = attr.fieldApiName!;
                      return (
                        <label
                          key={`modal-batch-eigene-${k}`}
                          className={`content-master-generate-field-check${
                            wawiFieldsLocked ? ' content-master-generate-field-check--locked' : ''
                          }`}
                          title={
                            wawiFieldsLocked
                              ? t('cm_entitlements_starter_wawi_disabled')
                              : undefined
                          }
                        >
                          <input
                            type="checkbox"
                            checked={!!generateBatchSelection[k]}
                            onChange={(e) => setBatchGenerateField(k, e.target.checked)}
                            disabled={generateAllLoading || wawiFieldsLocked}
                          />
                          <span>{attributeLikeLabel(attr, k)}</span>
                        </label>
                      );
                    })}
                  </>
                ) : null}

                {aiEligibleMerkmale.length > 0 ? (
                  <>
                    <span className="content-master-generate-field-group-title">
                      {t('content_master_generate_group_merkmal')}
                      {wawiFieldsLocked ? (
                        <ContentMasterProBadge className="content-master-pro-badge--inline" />
                      ) : null}
                    </span>
                    {aiEligibleMerkmale.map((m) => {
                      const k = m.fieldApiName!;
                      return (
                        <label
                          key={`modal-batch-merkmal-${k}`}
                          className={`content-master-generate-field-check${
                            wawiFieldsLocked ? ' content-master-generate-field-check--locked' : ''
                          }`}
                          title={
                            wawiFieldsLocked
                              ? t('cm_entitlements_starter_wawi_disabled')
                              : undefined
                          }
                        >
                          <input
                            type="checkbox"
                            checked={!!generateBatchSelection[k]}
                            onChange={(e) => setBatchGenerateField(k, e.target.checked)}
                            disabled={generateAllLoading || wawiFieldsLocked}
                          />
                          <span>{merkmalLabel(m, k)}</span>
                        </label>
                      );
                    })}
                  </>
                ) : null}
              </div>
            </div>

            <AdditionalContextInput
              initialValue={generateAdditionalContext}
              onStateChange={setGenerateAdditionalContext}
              disabled={generateAllLoading}
            />
            {generateAdditionalContextSurcharge != null &&
            generateAdditionalContextSurcharge > 0 ? (
              <div className="additional-context__surcharge" aria-live="polite">
                {t('cm_additional_context_surcharge', {
                  tokens: generateAdditionalContextSurcharge.toLocaleString(),
                })}
              </div>
            ) : null}

            <div className="content-master-modal__token-estimate" aria-live="polite">
              {generateModalTokenEstimate == null ? (
                <span className="content-master-modal__token-estimate-unknown">
                  {t('content_master_bulk_modal_token_estimate_unknown')}
                </span>
              ) : (
                <>
                  <span className="content-master-modal__token-estimate-label">
                    {t('content_master_bulk_modal_token_estimate_label')}
                  </span>
                  <span className="content-master-modal__token-estimate-value">
                    {/* Base estimate + the additional-context surcharge. */}
                    {Math.round(
                      generateModalTokenEstimate + (generateAdditionalContextSurcharge ?? 0)
                    ).toLocaleString()}{' '}
                    {t('content_master_bulk_modal_token_estimate_unit')}
                  </span>
                </>
              )}
            </div>

            <div className="content-master-modal__footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeGenerateModal}
                disabled={generateAllLoading}
              >
                {t('cancelText')}
              </button>
              <button
                type="button"
                className="btn btn-primary content-master-generate-all-btn"
                onClick={handleGenerateAll}
                disabled={
                  generateAllLoading ||
                  selectedGenerateFieldKeys.length === 0 ||
                  generateAdditionalContextTooLong ||
                  generateAdditionalContextParsing
                }
              >
                <ContentMasterSparkleIcon />
                {generateAllLoading ? t('loading_text') : t('content_master_generate_all_fields')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/*
        Translate field-picker modal. Shape mirrors the Generate modal so the
        two flows feel symmetric, but the eligibility rules differ:
          - Core fields → enabled,
          - attribut/eigene varchar (`nDatenTyp === 2`) non-readonly → enabled,
          - everything else (non-varchar attrs, readonly, all Merkmale) →
            rendered as disabled rows with an explanatory tooltip so users
            understand why these can't be translated, rather than silently
            hiding them.
        Only enabled rows participate in the selection map; "Select all
        translatable" / "Clear" only flip those keys.
      */}
      {isTranslateModalOpen ? (
        <div
          className="content-master-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !translateAllLoading) {
              closeTranslateModal();
            }
          }}
        >
          <div
            className="content-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="content-master-translate-modal-title"
          >
            <div className="content-master-modal__header">
              <h3 id="content-master-translate-modal-title" className="content-master-modal__title">
                {t('content_master_translate_pick_fields')}
              </h3>
              <button
                type="button"
                className="content-master-modal__close"
                onClick={closeTranslateModal}
                disabled={translateAllLoading}
                aria-label={t('cancelText')}
              >
                ×
              </button>
            </div>

            <div className="content-master-modal__toolbar">
              <button
                type="button"
                className="content-master-link-button"
                onClick={() => selectAllTranslatableBatchFields(true)}
                disabled={translateAllLoading}
              >
                {t('content_master_translate_select_all_translatable')}
              </button>
              <span className="content-master-modal__toolbar-sep" aria-hidden>
                ·
              </span>
              <button
                type="button"
                className="content-master-link-button"
                onClick={() => selectAllTranslatableBatchFields(false)}
                disabled={translateAllLoading}
              >
                {t('content_master_translate_clear_all')}
              </button>
              <span className="content-master-modal__toolbar-count">
                {selectedTranslateFieldKeys.length}
                {' / '}
                {Object.keys(translateBatchSelection).length}
              </span>
            </div>

            <div
              className="content-master-modal__body"
              role="group"
              aria-label={t('content_master_translate_pick_fields')}
            >
              <div className="content-master-generate-field-checkboxes">
                <span className="content-master-generate-field-group-title">
                  {t('content_master_generate_group_core')}
                </span>
                {CONTENT_MASTER_CORE_FIELD_KEYS.map((field) => (
                  <label
                    key={`modal-translate-core-${field}`}
                    className="content-master-generate-field-check"
                  >
                    <input
                      type="checkbox"
                      checked={!!translateBatchSelection[field]}
                      onChange={(e) => setBatchTranslateField(field, e.target.checked)}
                      disabled={translateAllLoading}
                    />
                    <span>{t(FIELD_LABELS[field])}</span>
                  </label>
                ))}

                {articleAttributes.length > 0 ? (
                  <>
                    <span className="content-master-generate-field-group-title">
                      {t('content_master_generate_group_wawi_attrs')}
                    </span>
                    {articleAttributes.map((attr) => {
                      const k = attr.fieldApiName;
                      if (!k) {
                        return null;
                      }
                      const translatable = canAiGenerateAttributeLike(attr);
                      const disabledReason = !translatable
                        ? attr.isReadOnly === true
                          ? t('content_master_translate_disabled_readonly_attr')
                          : t('content_master_translate_disabled_non_varchar_attr')
                        : undefined;
                      return (
                        <label
                          key={`modal-translate-attr-${k}`}
                          className="content-master-generate-field-check"
                          title={disabledReason}
                        >
                          <input
                            type="checkbox"
                            checked={!!translateBatchSelection[k]}
                            onChange={(e) => setBatchTranslateField(k, e.target.checked)}
                            disabled={translateAllLoading || !translatable}
                          />
                          <span>{attributeLikeLabel(attr, k)}</span>
                        </label>
                      );
                    })}
                  </>
                ) : null}

                {eigeneFelder.length > 0 ? (
                  <>
                    <span className="content-master-generate-field-group-title">
                      {t('content_master_generate_group_eigene_felder')}
                    </span>
                    {eigeneFelder.map((attr) => {
                      const k = attr.fieldApiName;
                      if (!k) {
                        return null;
                      }
                      const translatable = canAiGenerateAttributeLike(attr);
                      const disabledReason = !translatable
                        ? attr.isReadOnly === true
                          ? t('content_master_translate_disabled_readonly_attr')
                          : t('content_master_translate_disabled_non_varchar_attr')
                        : undefined;
                      return (
                        <label
                          key={`modal-translate-eigene-${k}`}
                          className="content-master-generate-field-check"
                          title={disabledReason}
                        >
                          <input
                            type="checkbox"
                            checked={!!translateBatchSelection[k]}
                            onChange={(e) => setBatchTranslateField(k, e.target.checked)}
                            disabled={translateAllLoading || !translatable}
                          />
                          <span>{attributeLikeLabel(attr, k)}</span>
                        </label>
                      );
                    })}
                  </>
                ) : null}

                {merkmale.length > 0 ? (
                  <>
                    <span className="content-master-generate-field-group-title">
                      {t('content_master_generate_group_merkmal')}
                    </span>
                    {merkmale.map((m) => {
                      const k = m.fieldApiName;
                      if (!k) {
                        return null;
                      }
                      return (
                        <label
                          key={`modal-translate-merkmal-${k}`}
                          className="content-master-generate-field-check"
                          title={t('content_master_translate_disabled_merkmal')}
                        >
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => undefined}
                            disabled
                          />
                          <span>{merkmalLabel(m, k)}</span>
                        </label>
                      );
                    })}
                  </>
                ) : null}
              </div>
            </div>

            <div className="content-master-modal__token-estimate" aria-live="polite">
              {translateModalTokenEstimate == null ? (
                <span className="content-master-modal__token-estimate-unknown">
                  {t('content_master_bulk_modal_token_estimate_unknown')}
                </span>
              ) : (
                <>
                  <span className="content-master-modal__token-estimate-label">
                    {t('content_master_bulk_modal_token_estimate_label')}
                  </span>
                  <span className="content-master-modal__token-estimate-value">
                    {Math.round(translateModalTokenEstimate).toLocaleString()}{' '}
                    {t('content_master_bulk_modal_token_estimate_unit')}
                  </span>
                </>
              )}
            </div>

            <div className="content-master-modal__footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeTranslateModal}
                disabled={translateAllLoading}
              >
                {t('cancelText')}
              </button>
              <button
                type="button"
                className="btn btn-primary content-master-generate-all-btn"
                onClick={handleTranslateAllFields}
                disabled={
                  translateAllLoading ||
                  !canTranslate ||
                  selectedTranslateFieldKeys.length === 0
                }
                title={
                  translateDisabledReason === 'same_language'
                    ? t('content_master_translate_same_language_hint')
                    : undefined
                }
              >
                <ContentMasterSparkleIcon />
                {translateAllLoading
                  ? t('loading_text')
                  : t('content_master_translate_all_fields')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {htmlPreviewModal}

      {/* Single, fixed action-bar for ALL product types. Save/Apply wiring
          branches by product type: marketplace channels drive the
          MarketplaceFieldsSection (which validates its category attributes),
          normal products use the core/wawi editor state. */}
      <div className="content-master-action-bar" role="group" aria-label={t('content_master_edit_title', { product: productName })}>
        <div className="content-master-action-bar__inner">
          {/* Slot the MarketplaceFieldsSection portals its validation-error
              chips into - the errors belong next to the Save/Apply they block
              (id, not a ref, so the section stays self-contained). */}
          <div
            id={ACTION_BAR_ERRORS_SLOT_ID}
            className="content-master-action-bar__errors"
          />
          <button
            type="button"
            className="content-master-action-bar__btn content-master-action-bar__btn--outline"
            onClick={
              isMarketplaceChannel
                ? () => marketplaceEditorRef.current?.save()
                : handleSaveDraft
            }
            disabled={
              !canCmWrite ||
              (isMarketplaceChannel
                ? !marketplaceEditorState.canSave ||
                  marketplaceEditorState.saving ||
                  marketplaceEditorState.applying
                : blockActionsWhileDetailLoading || !hasUnsavedChanges)
            }
            title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
          >
            {(isMarketplaceChannel ? marketplaceEditorState.saving : saveDraftLoading)
              ? t('loading_text')
              : t('content_master_save_as_draft')}
          </button>
          <button
            type="button"
            className="content-master-action-bar__btn content-master-action-bar__btn--primary"
            onClick={handleApply}
            disabled={
              !canCmWawiWrite ||
              (isMarketplaceChannel
                ? !marketplaceEditorState.canApply ||
                  marketplaceEditorState.saving ||
                  marketplaceEditorState.applying
                : blockActionsWhileDetailLoading || !hasUnsavedChanges)
            }
            title={!canCmWawiWrite ? t('subuser_no_permission_tooltip') : undefined}
          >
            <svg
              className="content-master-action-bar__btn-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3.5 8.5L6.5 11.5L12.5 5"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {(isMarketplaceChannel ? marketplaceEditorState.applying : applyLoading)
              ? t('loading_text')
              : t('content_master_apply_to_wawi')}
          </button>
        </div>
      </div>

      <ContentMasterApplyWarningModal
        isOpen={applyWarningOpen}
        onClose={() => setApplyWarningOpen(false)}
        flow="single"
        onConfirm={handleConfirmApplyWarning}
        isSubmitting={isMarketplaceChannel ? marketplaceEditorState.applying : applyLoading}
      />

      {/* Insufficient-tokens modal: shown reactively when generate / translate
          API calls are rejected with `errorCode === 'content_master_quota_exceeded'`.
          The slice flips `quotaExceededModalVisible` and skips setting
          `actionError`, so the regular toast does not fire for this code. */}
      <Modal
        isOpen={quotaExceededModalVisible}
        onClose={() => dispatch(setContentMasterQuotaModalVisible(false))}
        title={t('content_master_no_tokens_modal_title')}
        isCentered
        hideCloseButton
        className="content-master-confirm-modal content-master-confirm-modal--no-tokens"
      >
        <div className="content-master-confirm-modal__layout">
          <div className="content-master-confirm-modal__text">
            <p>{t('content_master_no_tokens_modal_body')}</p>
          </div>
          <div className="content-master-confirm-modal__illustration" aria-hidden />
        </div>
        <div className="content-master-confirm-modal__buttons">
          <button
            type="button"
            className="btn btn-secondary content-master-confirm-modal__btn-secondary"
            onClick={() => dispatch(setContentMasterQuotaModalVisible(false))}
          >
            {t('cancelText')}
          </button>
          <button
            type="button"
            className="btn btn-primary content-master-confirm-modal__btn-primary"
            onClick={() => {
              dispatch(setContentMasterQuotaModalVisible(false));
              navigate('/features/content-master/usage');
            }}
          >
            {t('content_master_no_tokens_modal_view_usage')}
            <svg
              className="content-master-confirm-modal__btn-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ContentMasterProductPage;
